import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { LayoutDashboard, Package, FolderOpen, ShoppingBag, Users, Tag, Star, Calendar, ChevronLeft, Download, Plus, Trash2, Edit, Search, X, Check, Upload, GripVertical, Image as ImageIcon, PanelLeft, CalendarOff, Mail, ShieldCheck } from 'lucide-react';
import { adminAPI, catalogAPI, miscAPI } from '../../api';
import { useAuth } from '../../context/AuthContext';
import SiteEditor from './SiteEditor';
import { toast } from 'sonner';
import { formatApiError } from '../../lib/apiError';

export default function AdminPanel() {
  const { user, loading } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    if (loading) return;
    if (!user || !user.admin_access) {
      navigate('/login');
    }
  }, [user, loading, navigate]);

  const permissions = useMemo(() => new Set(user?.permissions || []), [user]);
  const tabs = useMemo(() => [
    { path: '/admin', label: 'Dashboard', icon: LayoutDashboard, any: ['dashboard.view'] },
    { path: '/admin/products', label: 'Products', icon: Package, any: ['products.view', 'products.create', 'products.edit'] },
    { path: '/admin/categories', label: 'Categories', icon: FolderOpen, any: ['catalog.taxonomy.manage'] },
    { path: '/admin/orders', label: 'Orders', icon: ShoppingBag, any: ['orders.view'] },
    { path: '/admin/customers', label: permissions.has('users.view') ? 'Users' : 'Customers', icon: Users, any: ['customers.view', 'users.view'] },
    { path: '/admin/roles', label: 'Roles', icon: ShieldCheck, any: ['roles.view'] },
    { path: '/admin/audit', label: 'Admin Log', icon: ShieldCheck, any: ['audit.view'] },
    { path: '/admin/discounts', label: 'Discounts', icon: Tag, any: ['discounts.view', 'discounts.manage'] },
    { path: '/admin/specials', label: 'Shop Now', icon: Star, any: ['specials.view', 'specials.manage'] },
    { path: '/admin/prebook', label: 'Prebook', icon: Calendar, any: ['specials.manage'] },
    { path: '/admin/holidays', label: 'Holidays', icon: CalendarOff, any: ['calendar.view', 'calendar.manage'] },
    { path: '/admin/subscribers', label: 'Subscribers', icon: Mail, any: ['subscribers.view'] },
    { path: '/admin/site-editor', label: 'Site Editor', icon: PanelLeft, any: ['cms.edit'] },
  ].filter(tab => tab.any.some(permission => permissions.has(permission))), [permissions]);

  useEffect(() => {
    if (loading || !user?.admin_access) return;
    if (!tabs.length) return;
    if (!tabs.some(tab => tab.path === location.pathname)) navigate(tabs[0].path, { replace: true });
  }, [loading, user, location.pathname, navigate, tabs]);

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#2f3e36]"></div>
      </div>
    );
  }

  if (!user || !user.admin_access) return null;

  const activeRouteAllowed = tabs.some(tab => tab.path === location.pathname);
  if (!tabs.length) {
    return <main className="min-h-[60vh] flex items-center justify-center p-6"><p>No administrative sections are assigned to this account.</p></main>;
  }

  return (
    <div className="min-h-screen bg-[#fbfaf8]">
      <div className="flex">
        {/* Sidebar */}
        <aside className="w-56 bg-[#2d2d2d] text-white min-h-screen hidden md:block">
          <div className="p-4">
            <Link to="/" className="flex items-center gap-2 text-sm text-gray-400 hover:text-white mb-4">
              <ChevronLeft size={14} /> Back to Store
            </Link>
            <h2 className="text-sm font-semibold tracking-wider uppercase">Admin Panel</h2>
          </div>
          <nav className="px-2">
            {tabs.map(tab => (
              <Link key={tab.path} to={tab.path}
                className={`flex items-center gap-2 px-3 py-2.5 rounded-lg text-sm mb-0.5 transition-colors ${
                  location.pathname === tab.path ? 'bg-white/10 text-white' : 'text-gray-400 hover:text-white hover:bg-white/5'
                }`}>
                <tab.icon size={16} /> {tab.label}
              </Link>
            ))}
          </nav>
        </aside>

        {/* Main Content */}
        <main className="min-w-0 flex-1 p-4 md:p-6">
          {/* Mobile nav */}
          <div className="md:hidden flex overflow-x-auto gap-2 mb-4 pb-2">
            {tabs.map(tab => (
              <Link key={tab.path} to={tab.path}
                className={`flex items-center gap-1 px-3 py-2 rounded-lg text-xs whitespace-nowrap ${location.pathname === tab.path ? 'bg-[#2d2d2d] text-white' : 'bg-white border border-[#e7e3dd]'}`}>
                <tab.icon size={12} /> {tab.label}
              </Link>
            ))}
          </div>
          {activeRouteAllowed ? <AdminContent /> : <p role="status" aria-live="polite">Redirecting to an authorized section…</p>}
        </main>
      </div>
    </div>
  );
}

function AdminContent() {
  const location = useLocation();
  const path = location.pathname;
  if (path === '/admin/products') return <AdminProducts />;
  if (path === '/admin/categories') return <AdminCategories />;
  if (path === '/admin/orders') return <AdminOrders />;
  if (path === '/admin/customers') return <AdminCustomers />;
  if (path === '/admin/roles') return <AdminRoles />;
  if (path === '/admin/audit') return <AdminAudit />;
  if (path === '/admin/discounts') return <AdminDiscounts />;
  if (path === '/admin/specials') return <AdminSpecials />;
  if (path === '/admin/prebook') return <AdminPrebook />;
  if (path === '/admin/holidays') return <AdminHolidays />;
  if (path === '/admin/subscribers') return <AdminSubscribers />;
  if (path === '/admin/site-editor') return <SiteEditor />;
  return <AdminDashboard />;
}

// Dashboard
function AdminDashboard() {
  const { user } = useAuth();
  const canDownloadBackup = (user?.permissions || []).includes('backups.download');
  const [stats, setStats] = useState(null);
  const [configurationStatus, setConfigurationStatus] = useState(null);
  const [backupInProgress, setBackupInProgress] = useState(false);

  useEffect(() => {
    Promise.all([adminAPI.getStats(), adminAPI.getConfigurationStatus()])
      .then(([statsResponse, configurationResponse]) => {
        setStats(statsResponse.data);
        setConfigurationStatus(configurationResponse.data);
      })
      .catch(() => {});
  }, []);

  const handleFullBackup = async () => {
    setBackupInProgress(true);
    try {
      const queued = await adminAPI.createFullBackup();
      let job = queued.data;
      for (let attempt = 0; !job.download_ready && attempt < 900; attempt += 1) {
        if (job.status === 'failed' || job.status === 'expired') {
          throw new Error(job.status === 'failed' ? 'Backup generation failed' : 'Backup expired before download');
        }
        await new Promise(resolve => setTimeout(resolve, 2000));
        job = (await adminAPI.getFullBackupStatus(job.id)).data;
      }
      if (!job.download_ready) throw new Error('Backup generation timed out');
      const res = await adminAPI.downloadFullBackup(job.id);
      const disposition = res.headers?.['content-disposition'] || '';
      const filenameMatch = disposition.match(/filename\*?=(?:UTF-8''|")?([^";]+)/i);
      const filename = filenameMatch?.[1] ? decodeURIComponent(filenameMatch[1].trim()) : `petals-west-production-backup-${new Date().toISOString().slice(0, 10)}.zip`;
      const url = window.URL.createObjectURL(new Blob([res.data], { type: 'application/zip' }));
      const a = document.createElement('a');
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      toast.success('Full backup downloaded');
    } catch (err) {
      toast.error(formatApiError(err, 'Failed to generate full backup'));
    } finally {
      setBackupInProgress(false);
    }
  };

  if (!stats) return <p>Loading...</p>;

  const cards = [
    { label: 'Total Products', value: stats.total_products, color: 'bg-blue-50 text-blue-700' },
    { label: 'Total Orders', value: stats.total_orders, color: 'bg-green-50 text-green-700' },
    { label: 'Total Revenue', value: `$${stats.total_revenue?.toFixed(2)}`, color: 'bg-emerald-50 text-emerald-700' },
    { label: 'Customers', value: stats.total_customers, color: 'bg-purple-50 text-purple-700' },
    { label: 'Pending Approvals', value: stats.pending_approvals || 0, color: 'bg-orange-50 text-orange-700' },
    { label: 'Pending Orders', value: stats.pending_orders, color: 'bg-amber-50 text-amber-700' },
    { label: 'Low Stock Products', value: stats.low_stock_products, color: 'bg-red-50 text-red-700' },
  ];

  return (
    <div>
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-6">
        <h1 className="text-lg font-semibold tracking-wider uppercase">Dashboard</h1>
        {canDownloadBackup && <button
          data-testid="download-full-backup-btn"
          onClick={handleFullBackup}
          disabled={backupInProgress}
          className="inline-flex items-center justify-center gap-2 bg-[#2d2d2d] text-white px-4 py-2.5 rounded-lg text-sm font-medium hover:bg-[#3a3a3a] disabled:opacity-50 disabled:cursor-not-allowed"
          title="Download PostgreSQL data, metadata, indexes and referenced image files"
        >
          <Download size={15} />
          {backupInProgress ? 'Preparing backup...' : 'Download Full Backup'}
        </button>}
      </div>
      <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
        {cards.map((card, i) => (
          <div key={i} className={`${card.color} rounded-xl p-4`}>
            <p className="text-xs opacity-70 uppercase tracking-wider">{card.label}</p>
            <p className="text-2xl font-semibold mt-1">{card.value}</p>
          </div>
        ))}
      </div>
      {configurationStatus?.missing?.length > 0 && (
        <section className="mt-6 rounded-xl border border-amber-300 bg-amber-50 p-4" aria-labelledby="configuration-required-title">
          <h2 id="configuration-required-title" className="font-semibold text-amber-950">Configuration required before commercial activation</h2>
          <p className="mt-1 text-sm text-amber-900">These features remain safely disabled until their runtime configuration is complete:</p>
          <ul className="mt-2 list-disc space-y-1 pl-5 text-sm text-amber-900">
            {configurationStatus.missing.map(item => <li key={item}>{item}</li>)}
          </ul>
        </section>
      )}
    </div>
  );
}

// Reusable Color Selector with "Add New" capability
function ColorSelect({ value, onChange, testId }) {
  const [colors, setColors] = useState([]);
  const [open, setOpen] = useState(false);
  const [newColor, setNewColor] = useState('');
  const [adding, setAdding] = useState(false);
  const [filter, setFilter] = useState('');
  const ref = useRef(null);
  const searchRef = useRef(null);

  useEffect(() => {
    miscAPI.getColors().then(r => setColors(r.data.colors)).catch(() => {});
  }, []);

  // Close on outside click/touch
  useEffect(() => {
    const handler = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener('mousedown', handler);
    document.addEventListener('touchstart', handler, { passive: true });
    return () => {
      document.removeEventListener('mousedown', handler);
      document.removeEventListener('touchstart', handler);
    };
  }, []);

  // Focus search input after open (delayed for mobile compatibility)
  useEffect(() => {
    if (open && searchRef.current) {
      const timer = setTimeout(() => searchRef.current?.focus(), 50);
      return () => clearTimeout(timer);
    }
  }, [open]);

  const filtered = colors.filter(c => c.toLowerCase().includes(filter.toLowerCase()));

  const handleAdd = async () => {
    const name = newColor.trim();
    if (!name) return;
    setAdding(true);
    try {
      await adminAPI.addColor(name);
      setColors(prev => [...prev, name].sort((a, b) => a.localeCompare(b)));
      onChange(name);
      setNewColor('');
      setOpen(false);
      toast.success(`Color "${name}" added`);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to add color');
    } finally { setAdding(false); }
  };

  const handleSelect = (c) => {
    onChange(c);
    setOpen(false);
    setFilter('');
  };

  return (
    <div ref={ref} className="relative" style={{ WebkitTapHighlightColor: 'transparent' }}>
      <button
        type="button"
        data-testid={testId || 'color-select'}
        onClick={() => { setOpen(!open); setFilter(''); }}
        className="w-full px-3 py-2 border border-[#e7e3dd] rounded-lg text-sm bg-white text-left flex justify-between items-center hover:border-[#2f6b4f]"
        style={{ touchAction: 'manipulation' }}
      >
        <span className={value ? 'text-[#2d2d2d]' : 'text-[#b0ada6]'}>{value || 'Select color...'}</span>
        <svg className={`w-4 h-4 text-[#6f6a63] transition-transform ${open ? 'rotate-180' : ''}`} fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" /></svg>
      </button>
      {open && (
        <div
          className="absolute z-50 mt-1 w-full bg-white border border-[#e7e3dd] rounded-lg shadow-lg flex flex-col"
          style={{ maxHeight: '16rem' }}
        >
          <div className="p-2 border-b border-[#f0efed] flex-shrink-0">
            <input
              ref={searchRef}
              type="text"
              inputMode="search"
              autoComplete="off"
              autoCorrect="off"
              spellCheck="false"
              placeholder="Search colors..."
              value={filter}
              onChange={e => setFilter(e.target.value)}
              onTouchStart={e => e.stopPropagation()}
              className="w-full px-2.5 py-1.5 text-xs border border-[#e7e3dd] rounded focus:outline-none focus:border-[#2f6b4f]"
              style={{ fontSize: '16px', touchAction: 'manipulation' }}
            />
          </div>
          <div
            className="flex-1"
            style={{
              overflowY: 'auto',
              WebkitOverflowScrolling: 'touch',
              overscrollBehavior: 'contain',
              touchAction: 'pan-y',
            }}
          >
            <button type="button" onClick={() => handleSelect('')} onTouchEnd={(e) => { e.preventDefault(); handleSelect(''); }} className="w-full text-left px-3 py-2 text-xs text-[#b0ada6] hover:bg-[#fbfaf8] active:bg-[#f0efed]">
              No color
            </button>
            {filtered.map(c => (
              <button
                key={c}
                type="button"
                onClick={() => handleSelect(c)}
                onTouchEnd={(e) => { e.preventDefault(); handleSelect(c); }}
                className={`w-full text-left px-3 py-2 text-xs hover:bg-[#f0efed] active:bg-[#e7e3dd] ${c === value ? 'bg-[#f0efed] font-medium' : ''}`}
              >
                {c}
              </button>
            ))}
            {filtered.length === 0 && filter && (
              <p className="px-3 py-2 text-xs text-[#b0ada6]">No colors match "{filter}"</p>
            )}
          </div>
          <div className="border-t border-[#f0efed] p-2 flex gap-1.5 flex-shrink-0">
            <input
              type="text"
              inputMode="text"
              autoComplete="off"
              placeholder="New color name..."
              value={newColor}
              onChange={e => setNewColor(e.target.value)}
              onKeyDown={e => e.key === 'Enter' && (e.preventDefault(), handleAdd())}
              onTouchStart={e => e.stopPropagation()}
              className="flex-1 px-2.5 py-1.5 text-xs border border-[#e7e3dd] rounded focus:outline-none focus:border-[#2f6b4f]"
              style={{ fontSize: '16px', touchAction: 'manipulation' }}
              data-testid="color-new-input"
            />
            <button
              type="button"
              onClick={handleAdd}
              disabled={adding || !newColor.trim()}
              data-testid="color-add-btn"
              className="px-3 py-1.5 text-xs bg-[#2f6b4f] text-white rounded hover:bg-[#265a42] disabled:opacity-40 active:bg-[#1e4a35]"
              style={{ touchAction: 'manipulation' }}
            >
              <Plus size={12} />
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

// Products
function AdminProducts() {
  const { user } = useAuth();
  const permissions = new Set(user?.permissions || []);
  const canImport = permissions.has('products.import');
  const canCreate = permissions.has('products.create');
  const canEdit = permissions.has('products.edit');
  const canDelete = permissions.has('products.delete');
  const [products, setProducts] = useState([]);
  const [search, setSearch] = useState('');
  const [editing, setEditing] = useState(null);
  const [showCreate, setShowCreate] = useState(false);
  const [importing, setImporting] = useState(false);
  const [importResult, setImportResult] = useState(null);
  const fileInputRef = useRef(null);
  const [page, setPage] = useState(1);
  const [totalProducts, setTotalProducts] = useState(0);
  const LIMIT = 50;
  const totalPages = Math.max(1, Math.ceil(totalProducts / LIMIT));

  const loadProducts = () => {
    adminAPI.getProducts({ search, limit: LIMIT, page }).then(res => {
      setProducts(res.data.products);
      setTotalProducts(res.data.total || 0);
    }).catch(() => {});
  };
  useEffect(loadProducts, [search, page]);

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this product?')) return;
    await adminAPI.deleteProduct(id);
    toast.success('Product deleted');
    loadProducts();
  };

  const handleToggleVisibility = async (id, currentActive) => {
    try {
      await adminAPI.toggleVisibility(id, !currentActive);
      toast.success(currentActive ? 'Product hidden' : 'Product visible');
      loadProducts();
    } catch (err) { toast.error('Failed to update visibility'); }
  };

  const handleSave = async (data) => {
    try {
      if (editing) {
        await adminAPI.updateProduct(editing._id, data);
        toast.success('Product updated');
      } else {
        await adminAPI.createProduct(data);
        toast.success('Product created');
      }
      setEditing(null);
      setShowCreate(false);
      loadProducts();
    } catch (err) {
      toast.error(formatApiError(err, 'Failed'));
    }
  };

  const handleDownloadTemplate = async () => {
    try {
      const res = await adminAPI.downloadInventoryTemplate();
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement('a');
      a.href = url;
      a.download = 'petals_west_inventory_template.xlsx';
      a.click();
      window.URL.revokeObjectURL(url);
      toast.success('Template downloaded');
    } catch { toast.error('Failed to download template'); }
  };

  const handleImportFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!file.name.toLowerCase().endsWith('.xlsx')) {
      const detail = 'Only .xlsx files are supported';
      toast.error(detail);
      setImportResult({ success: false, error: detail });
      e.target.value = '';
      return;
    }
    setImporting(true);
    setImportResult(null);
    try {
      const formData = new FormData();
      formData.append('file', file);
      const res = await adminAPI.importInventory(formData);
      setImportResult(res.data);
      toast.success(`Updated ${res.data.updated} products`);
      loadProducts();
    } catch (err) {
      const detail = formatApiError(err, 'Import failed');
      toast.error(detail);
      setImportResult({ success: false, error: detail });
    } finally {
      setImporting(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  return (
    <div>
      <div className="flex items-center justify-between mb-4">
        <h1 className="text-lg font-semibold tracking-wider uppercase">Products ({totalProducts})</h1>
        <div className="flex items-center gap-2">
          {canImport && <button data-testid="download-template-btn" onClick={handleDownloadTemplate} className="flex items-center gap-1 border border-[#e7e3dd] text-[#2d2d2d] px-3 py-2 rounded-lg text-sm hover:bg-gray-50"><Download size={14} /> Template</button>}
          {canImport &&
          <label data-testid="import-inventory-btn" className={`flex items-center gap-1 border border-[#2f6b4f] text-[#2f6b4f] px-3 py-2 rounded-lg text-sm hover:bg-[#e7f1ea] cursor-pointer ${importing ? 'opacity-50 pointer-events-none' : ''}`}>
            <Upload size={14} /> {importing ? 'Importing...' : 'Import'}
            <input ref={fileInputRef} type="file" accept=".xlsx" onChange={handleImportFile} className="hidden" />
          </label>}
          {canCreate && <button onClick={() => { setEditing(null); setShowCreate(true); }} className="flex items-center gap-1 bg-[#2d2d2d] text-white px-4 py-2 rounded-lg text-sm"><Plus size={14} /> Add Product</button>}
        </div>
      </div>
      <p className="text-sm text-[#6f6a63] mb-4">Products appear in Our Varieties. Shop Now is a separate catalog; adding a product there does not add it here.</p>

      {/* Import Result */}
      {importResult && (
        <div className={`mb-4 p-4 rounded-xl border text-sm ${importResult.success ? 'bg-[#e7f1ea] border-[#2f6b4f]' : 'bg-red-50 border-red-300'}`}>
          {importResult.success ? (
            <div>
              <p className="font-medium text-[#2f6b4f]">{importResult.message || `Import complete: ${importResult.updated} products updated atomically.`}</p>
              {importResult.errors?.length > 0 && (
                <ul className="mt-2 text-xs text-[#6f6a63] space-y-0.5">
                  {importResult.errors.map((err, i) => <li key={i}>{err}</li>)}
                </ul>
              )}
            </div>
          ) : (
            <p className="text-red-700">{importResult.error || 'Import failed'}</p>
          )}
          <button onClick={() => setImportResult(null)} className="text-xs underline mt-2 text-[#6f6a63]">Dismiss</button>
        </div>
      )}

      <div className="mb-4 flex gap-2">
        <div className="relative flex-1">
          <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
          <input type="text" value={search} onChange={(e) => { setSearch(e.target.value); setPage(1); }} placeholder="Search products..." className="w-full pl-9 pr-4 py-2 border border-[#e7e3dd] rounded-lg text-sm bg-white focus:outline-none" />
        </div>
      </div>

      {(editing || showCreate) && (
        <ProductForm product={editing} onSave={handleSave} onImageSaved={loadProducts} onCancel={() => { setEditing(null); setShowCreate(false); }} />
      )}

      <div className="bg-white rounded-xl border border-[#e7e3dd] overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-[#fbfaf8]">
            <tr>
              <th className="text-left px-4 py-3 text-xs uppercase tracking-wider text-[#6f6a63]">Product</th>
              <th className="text-left px-4 py-3 text-xs uppercase tracking-wider text-[#6f6a63] hidden md:table-cell">Category</th>
              <th className="text-center px-4 py-3 text-xs uppercase tracking-wider text-[#6f6a63]">Stock</th>
              <th className="text-center px-4 py-3 text-xs uppercase tracking-wider text-[#6f6a63]">Price</th>
              <th className="text-center px-4 py-3 text-xs uppercase tracking-wider text-[#6f6a63]">Visible</th>
              <th className="text-right px-4 py-3 text-xs uppercase tracking-wider text-[#6f6a63]">Actions</th>
            </tr>
          </thead>
          <tbody>
            {products.map(p => (
              <tr key={p._id} className={`border-t border-[#e7e3dd] hover:bg-gray-50 ${p.is_active === false ? 'opacity-50' : ''}`}>
                <td className="px-4 py-3">
                  <div className="flex items-center gap-2">
                    <img src={p.images?.[0]} alt="" className="w-8 h-8 rounded object-cover" />
                    <div>
                      <p className="font-medium text-xs">{p.name}</p>
                      <p className="text-[10px] text-[#6f6a63]">{p.sku}</p>
                    </div>
                  </div>
                </td>
                <td className="px-4 py-3 text-xs text-[#6f6a63] hidden md:table-cell">{p.category}</td>
                <td className="px-4 py-3 text-center">
                  <span className={`text-xs px-2 py-0.5 rounded ${p.stock <= 0 ? 'bg-red-50 text-red-600' : p.stock <= (p.low_stock_threshold || 10) ? 'bg-amber-50 text-amber-600' : 'bg-green-50 text-green-600'}`}>{p.stock}</span>
                </td>
                <td className="px-4 py-3 text-center text-xs">${p.tier_pricing?.[0]?.price?.toFixed(2) || '0.00'}</td>
                <td className="px-4 py-3 text-center">
                  {canEdit ? <button
                    data-testid={`toggle-visibility-${p._id}`}
                    onClick={() => handleToggleVisibility(p._id, p.is_active !== false)}
                    className={`p-1 rounded transition-colors ${p.is_active !== false ? 'text-[#2f6b4f] hover:bg-green-50' : 'text-[#b0ada6] hover:bg-gray-100'}`}
                    title={p.is_active !== false ? 'Visible — click to hide' : 'Hidden — click to show'}
                  >
                    {p.is_active !== false
                      ? <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/><circle cx="12" cy="12" r="3"/></svg>
                      : <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94"/><path d="M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19"/><path d="M1 1l22 22"/><path d="M14.12 14.12a3 3 0 1 1-4.24-4.24"/></svg>
                    }
                  </button> : <span className="text-xs">{p.is_active !== false ? 'Yes' : 'No'}</span>}
                </td>
                <td className="px-4 py-3 text-right">
                  {canEdit && <button onClick={() => { setEditing(p); setShowCreate(false); }} className="text-blue-600 hover:text-blue-800 mr-2"><Edit size={14} /></button>}
                  {canDelete && <button onClick={() => handleDelete(p._id)} className="text-red-500 hover:text-red-700"><Trash2 size={14} /></button>}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {/* Pagination */}
      {totalPages > 1 && (
        <div className="flex items-center justify-between mt-4">
          <p className="text-xs text-[#6f6a63]">
            Showing {(page - 1) * LIMIT + 1}-{Math.min(page * LIMIT, totalProducts)} of {totalProducts}
          </p>
          <div className="flex gap-1">
            <button
              data-testid="products-prev-page"
              disabled={page <= 1}
              onClick={() => setPage(p => Math.max(1, p - 1))}
              className="px-3 py-1.5 rounded-lg text-sm border border-[#e7e3dd] hover:bg-gray-50 disabled:opacity-30 disabled:cursor-not-allowed"
            >
              Prev
            </button>
            {Array.from({ length: totalPages }, (_, i) => i + 1)
              .filter(p => p === 1 || p === totalPages || Math.abs(p - page) <= 2)
              .reduce((acc, p, i, arr) => {
                if (i > 0 && p - arr[i - 1] > 1) acc.push('...');
                acc.push(p);
                return acc;
              }, [])
              .map((p, i) =>
                p === '...' ? (
                  <span key={`ellipsis-${i}`} className="px-2 py-1.5 text-sm text-[#6f6a63]">...</span>
                ) : (
                  <button
                    key={p}
                    data-testid={`products-page-${p}`}
                    onClick={() => setPage(p)}
                    className={`px-3 py-1.5 rounded-lg text-sm ${p === page ? 'bg-[#2d2d2d] text-white' : 'border border-[#e7e3dd] hover:bg-gray-50'}`}
                  >
                    {p}
                  </button>
                )
              )}
            <button
              data-testid="products-next-page"
              disabled={page >= totalPages}
              onClick={() => setPage(p => Math.min(totalPages, p + 1))}
              className="px-3 py-1.5 rounded-lg text-sm border border-[#e7e3dd] hover:bg-gray-50 disabled:opacity-30 disabled:cursor-not-allowed"
            >
              Next
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

function ProductForm({ product, onSave, onImageSaved, onCancel }) {
  const [form, setForm] = useState({
    name: '', sku: '', category: '', subcategory: '', description_short: '', description_long: '',
    stock: 0, low_stock_threshold: 10, pack_size: '', is_active: true,
    color: '', stem_length: '', origin: '',
    tier_pricing: [{ min_qty: 1, max_qty: 4, price: 0 }, { min_qty: 5, max_qty: 9, price: 0 }, { min_qty: 10, max_qty: null, price: 0 }],
    tags: [], images: [], attributes: {}
  });
  const [uploading, setUploading] = useState(false);
  const [pendingImageSave, setPendingImageSave] = useState(false);
  const [dragOver, setDragOver] = useState(false);
  const fileInputRef = React.useRef(null);

  useEffect(() => {
    if (product) {
      setForm({
        name: product.name || '', sku: product.sku || '', category: product.category || '',
        subcategory: product.subcategory || '', description_short: product.description_short || '',
        description_long: product.description_long || '', stock: product.stock || 0,
        low_stock_threshold: product.low_stock_threshold || 10, pack_size: product.pack_size || '',
        color: product.color || '', stem_length: product.stem_length || '', origin: product.origin || '',
        is_active: product.is_active !== false,
        tier_pricing: product.tier_pricing?.length ? product.tier_pricing : [{ min_qty: 1, max_qty: 4, price: 0 }, { min_qty: 5, max_qty: 9, price: 0 }, { min_qty: 10, max_qty: null, price: 0 }],
        tags: product.tags || [], images: product.images || [], attributes: product.attributes || {}
      });
    }
  }, [product]);

  const handleSubmit = (e) => {
    e.preventDefault();
    onSave(form);
  };

  const handleFileSelect = async (files) => {
    if (!files || files.length === 0) return;
    const validFiles = Array.from(files).filter(f => f.type.startsWith('image/') && f.size <= 5 * 1024 * 1024);
    if (validFiles.length === 0) {
      toast.error('Only image files under 5MB are allowed');
      return;
    }
    setUploading(true);
    try {
      let images = [...form.images];
      let uploaded = 0;
      for (const file of validFiles) {
        const res = await adminAPI.uploadImage(file);
        images = [...images, res.data.url];
        if (product) {
          await adminAPI.updateProduct(product._id, { images });
          onImageSaved();
        } else {
          setPendingImageSave(true);
        }
        setForm(f => ({ ...f, images }));
        uploaded += 1;
      }
      toast.success(product ? `${uploaded} image(s) saved to product` : `${uploaded} image(s) uploaded. Click Save to attach them to the new product.`);
    } catch (err) {
      toast.error('Image upload or product save failed');
    } finally {
      setUploading(false);
    }
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setDragOver(false);
    handleFileSelect(e.dataTransfer.files);
  };

  const handleCancel = () => {
    if (uploading) return;
    if (pendingImageSave && !window.confirm('Uploaded images are not attached to a product until you click Save. Discard this product?')) return;
    onCancel();
  };

  const removeImage = (index) => {
    setForm(f => ({ ...f, images: f.images.filter((_, i) => i !== index) }));
  };

  const moveImage = (from, to) => {
    if (to < 0 || to >= form.images.length) return;
    setForm(f => {
      const imgs = [...f.images];
      const [moved] = imgs.splice(from, 1);
      imgs.splice(to, 0, moved);
      return { ...f, images: imgs };
    });
  };

  return (
    <div className="bg-white border border-[#e7e3dd] rounded-xl p-6 mb-4">
      <div className="flex justify-between items-center mb-4">
        <h3 className="text-sm font-semibold">{product ? 'Edit Product' : 'New Product'}</h3>
        <button onClick={handleCancel} disabled={uploading}><X size={18} /></button>
      </div>
      <form onSubmit={handleSubmit} className="space-y-4">
        <div className="grid sm:grid-cols-2 gap-4">
          <div><label className="text-xs text-[#6f6a63] block mb-1">Name *</label><input required value={form.name} onChange={e => setForm(f => ({ ...f, name: e.target.value }))} className="w-full px-3 py-2 border border-[#e7e3dd] rounded-lg text-sm" /></div>
          <div><label className="text-xs text-[#6f6a63] block mb-1">SKU</label><input value={form.sku} onChange={e => setForm(f => ({ ...f, sku: e.target.value }))} className="w-full px-3 py-2 border border-[#e7e3dd] rounded-lg text-sm" /></div>
          <div><label className="text-xs text-[#6f6a63] block mb-1">Category</label><input value={form.category} onChange={e => setForm(f => ({ ...f, category: e.target.value }))} className="w-full px-3 py-2 border border-[#e7e3dd] rounded-lg text-sm" /></div>
          <div><label className="text-xs text-[#6f6a63] block mb-1">Subcategory</label><input value={form.subcategory} onChange={e => setForm(f => ({ ...f, subcategory: e.target.value }))} className="w-full px-3 py-2 border border-[#e7e3dd] rounded-lg text-sm" /></div>
          <div><label className="text-xs text-[#6f6a63] block mb-1">Stock</label><input type="number" value={form.stock} onChange={e => setForm(f => ({ ...f, stock: parseInt(e.target.value) || 0 }))} className="w-full px-3 py-2 border border-[#e7e3dd] rounded-lg text-sm" /></div>
          <div><label className="text-xs text-[#6f6a63] block mb-1">Pack Size</label><input value={form.pack_size} onChange={e => setForm(f => ({ ...f, pack_size: e.target.value }))} className="w-full px-3 py-2 border border-[#e7e3dd] rounded-lg text-sm" /></div>
          <div><label className="text-xs text-[#6f6a63] block mb-1">Color</label>
            <ColorSelect value={form.color} onChange={v => setForm(f => ({ ...f, color: v }))} testId="product-color-select" />
          </div>
          <div><label className="text-xs text-[#6f6a63] block mb-1">Stem Length</label><input value={form.stem_length} onChange={e => setForm(f => ({ ...f, stem_length: e.target.value }))} placeholder='e.g. 60cm' className="w-full px-3 py-2 border border-[#e7e3dd] rounded-lg text-sm" /></div>
          <div><label className="text-xs text-[#6f6a63] block mb-1">Origin</label><input value={form.origin} onChange={e => setForm(f => ({ ...f, origin: e.target.value }))} placeholder='e.g. Colombia' className="w-full px-3 py-2 border border-[#e7e3dd] rounded-lg text-sm" /></div>
        </div>
        <div><label className="text-xs text-[#6f6a63] block mb-1">Short Description</label><input value={form.description_short} onChange={e => setForm(f => ({ ...f, description_short: e.target.value }))} className="w-full px-3 py-2 border border-[#e7e3dd] rounded-lg text-sm" /></div>
        <div><label className="text-xs text-[#6f6a63] block mb-1">Long Description</label><textarea rows={3} value={form.description_long} onChange={e => setForm(f => ({ ...f, description_long: e.target.value }))} className="w-full px-3 py-2 border border-[#e7e3dd] rounded-lg text-sm" /></div>

        {/* Product Images */}
        <div>
          <label className="text-xs text-[#6f6a63] block mb-2">Product Images</label>
          {/* Current images grid */}
          {form.images.length > 0 && (
            <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-6 gap-3 mb-3">
              {form.images.map((img, i) => (
                <div key={i} className="relative group border border-[#e7e3dd] rounded-lg overflow-hidden aspect-square bg-gray-50">
                  <img src={img} alt={`Product ${i + 1}`} className="w-full h-full object-cover" />
                  {/* Badge for first image */}
                  {i === 0 && (
                    <span className="absolute top-1 left-1 bg-[#2f3e36] text-white text-[9px] px-1.5 py-0.5 rounded">Main</span>
                  )}
                  {/* Overlay controls */}
                  <div className="absolute inset-0 bg-black/40 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center gap-1.5">
                    {i > 0 && (
                      <button type="button" onClick={() => moveImage(i, i - 1)} className="bg-white/90 text-[#2d2d2d] p-1 rounded hover:bg-white text-xs" title="Move left">
                        <ChevronLeft size={14} />
                      </button>
                    )}
                    <button type="button" onClick={() => removeImage(i)} className="bg-red-500/90 text-white p-1 rounded hover:bg-red-600" title="Remove">
                      <Trash2 size={14} />
                    </button>
                    {i < form.images.length - 1 && (
                      <button type="button" onClick={() => moveImage(i, i + 1)} className="bg-white/90 text-[#2d2d2d] p-1 rounded hover:bg-white text-xs" title="Move right">
                        <ChevronLeft size={14} className="rotate-180" />
                      </button>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
          {/* Drop zone */}
          <div
            data-testid="product-image-dropzone"
            onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
            onDragLeave={() => setDragOver(false)}
            onDrop={handleDrop}
            onClick={() => fileInputRef.current?.click()}
            className={`border-2 border-dashed rounded-lg p-6 text-center cursor-pointer transition-colors ${
              dragOver ? 'border-[#2f6b4f] bg-[#e7f1ea]/30' : 'border-[#d5d0c8] hover:border-[#2f6b4f] hover:bg-[#fbfaf8]'
            } ${uploading ? 'pointer-events-none opacity-60' : ''}`}
          >
            <input
              ref={fileInputRef}
              type="file"
              accept="image/jpeg,image/png,image/webp,image/gif"
              multiple
              className="hidden"
              onChange={(e) => handleFileSelect(e.target.files)}
            />
            {uploading ? (
              <div className="flex flex-col items-center gap-2">
                <div className="w-6 h-6 border-2 border-[#2f6b4f] border-t-transparent rounded-full animate-spin" />
                <p className="text-xs text-[#6f6a63]">Uploading...</p>
              </div>
            ) : (
              <div className="flex flex-col items-center gap-2">
                <Upload size={24} className="text-[#6f6a63]" />
                <p className="text-xs text-[#6f6a63]">
                  <span className="text-[#2f6b4f] font-medium">Click to upload</span> or drag and drop
                </p>
                <p className="text-[10px] text-[#999]">JPEG, PNG, WebP, GIF (max 5MB each)</p>
              </div>
            )}
          </div>
        </div>
        
        {/* Tier Pricing */}
        <div>
          <label className="text-xs text-[#6f6a63] block mb-2">Tier Pricing</label>
          {form.tier_pricing.map((tier, i) => (
            <div key={i} className="flex gap-2 mb-2">
              <input type="number" placeholder="Min" value={tier.min_qty} onChange={e => { const t = [...form.tier_pricing]; t[i].min_qty = parseInt(e.target.value) || 0; setForm(f => ({ ...f, tier_pricing: t })); }} className="w-20 px-2 py-1 border border-[#e7e3dd] rounded text-sm" />
              <span className="text-[#6f6a63] self-center">-</span>
              <input type="number" placeholder="Max (empty=+)" value={tier.max_qty || ''} onChange={e => { const t = [...form.tier_pricing]; t[i].max_qty = e.target.value ? parseInt(e.target.value) : null; setForm(f => ({ ...f, tier_pricing: t })); }} className="w-20 px-2 py-1 border border-[#e7e3dd] rounded text-sm" />
              <span className="text-[#6f6a63] self-center">$</span>
              <input type="number" step="0.01" placeholder="Price" value={tier.price} onChange={e => { const t = [...form.tier_pricing]; t[i].price = parseFloat(e.target.value) || 0; setForm(f => ({ ...f, tier_pricing: t })); }} className="w-24 px-2 py-1 border border-[#e7e3dd] rounded text-sm" />
            </div>
          ))}
        </div>

        <div className="flex gap-3">
          <button type="submit" disabled={uploading} className="bg-[#2d2d2d] text-white px-6 py-2 rounded-lg text-sm disabled:opacity-50">Save</button>
          <button type="button" onClick={handleCancel} disabled={uploading} className="border border-[#e7e3dd] px-6 py-2 rounded-lg text-sm disabled:opacity-50">Cancel</button>
        </div>
      </form>
    </div>
  );
}

// Orders
function AdminOrders() {
  const { user } = useAuth();
  const permissions = new Set(user?.permissions || []);
  const canExport = permissions.has('orders.export');
  const [orders, setOrders] = useState([]);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('');

  const loadOrders = () => {
    const params = { limit: 100 };
    if (search) params.search = search;
    if (statusFilter) params.status = statusFilter;
    adminAPI.getOrders(params).then(res => setOrders(res.data.orders)).catch(() => {});
  };
  useEffect(loadOrders, [search, statusFilter]);

  const handleStatusChange = async (orderId, newStatus) => {
    try {
      await adminAPI.updateOrderStatus(orderId, newStatus);
      toast.success('Status updated');
      loadOrders();
    } catch { toast.error('Failed'); }
  };

  const handleExport = async () => {
    try {
      const params = {};
      if (statusFilter) params.status = statusFilter;
      const res = await adminAPI.exportOrders(params);
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement('a');
      a.href = url;
      a.download = 'orders.csv';
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      toast.success('Export downloaded');
    } catch { toast.error('Export failed'); }
  };

  const statuses = ['pending', 'paid', 'processing', 'completed', 'cancelled'];

  return (
    <div>
      <div className="flex items-center justify-between mb-4">
        <h1 className="text-lg font-semibold tracking-wider uppercase">Orders ({orders.length})</h1>
        {canExport && <button onClick={handleExport} className="flex items-center gap-1 bg-[#2f6b4f] text-white px-4 py-2 rounded-lg text-sm"><Download size={14} /> Export CSV</button>}
      </div>
      <div className="flex gap-2 mb-4">
        <input type="text" value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search orders..." className="flex-1 px-3 py-2 border border-[#e7e3dd] rounded-lg text-sm bg-white" />
        <select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)} className="px-3 py-2 border border-[#e7e3dd] rounded-lg text-sm bg-white">
          <option value="">All Statuses</option>
          {statuses.map(s => <option key={s} value={s} className="capitalize">{s}</option>)}
        </select>
      </div>
      <div className="bg-white rounded-xl border border-[#e7e3dd] overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-[#fbfaf8]">
            <tr>
              <th className="text-left px-4 py-3 text-xs uppercase tracking-wider text-[#6f6a63]">Order</th>
              <th className="text-left px-4 py-3 text-xs uppercase tracking-wider text-[#6f6a63]">Customer</th>
              <th className="text-center px-4 py-3 text-xs uppercase tracking-wider text-[#6f6a63]">Total</th>
              <th className="text-center px-4 py-3 text-xs uppercase tracking-wider text-[#6f6a63]">Status</th>
              <th className="text-center px-4 py-3 text-xs uppercase tracking-wider text-[#6f6a63]">Date</th>
            </tr>
          </thead>
          <tbody>
            {orders.map(o => (
              <tr key={o._id} className="border-t border-[#e7e3dd]">
                <td className="px-4 py-3"><p className="text-xs font-medium">{o.order_number}</p><p className="text-[10px] text-[#6f6a63]">{o.items?.length} items</p></td>
                <td className="px-4 py-3"><p className="text-xs">{o.customer_name}</p><p className="text-[10px] text-[#6f6a63]">{o.customer_email}</p></td>
                <td className="px-4 py-3 text-center text-xs font-medium">${o.total?.toFixed(2)}</td>
                <td className="px-4 py-3 text-center">
                  {(permissions.has('orders.tracking.update') || permissions.has('orders.cancel') || permissions.has('orders.payment.confirm')) ? <select value={o.status} onChange={(e) => handleStatusChange(o._id, e.target.value)}
                    className={`text-xs px-2 py-1 rounded border-0 ${o.status === 'completed' ? 'bg-green-50 text-green-700' : o.status === 'cancelled' ? 'bg-red-50 text-red-700' : o.status === 'paid' ? 'bg-blue-50 text-blue-700' : 'bg-amber-50 text-amber-700'}`}>
                    <option value={o.status}>{o.status}</option>
                    {permissions.has('orders.payment.confirm') && o.status === 'pending' && <option value="paid">paid</option>}
                    {permissions.has('orders.tracking.update') && o.status === 'paid' && <option value="processing">processing</option>}
                    {permissions.has('orders.tracking.update') && o.status === 'processing' && <option value="completed">completed</option>}
                    {permissions.has('orders.cancel') && !['completed', 'cancelled'].includes(o.status) && <option value="cancelled">cancelled</option>}
                  </select> : <span className="text-xs">{o.status}</span>}
                </td>
                <td className="px-4 py-3 text-center text-xs text-[#6f6a63]">{new Date(o.created_at).toLocaleDateString()}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

// Categories
function AdminCategories() {
  const [categories, setCategories] = useState([]);
  useEffect(() => {
    catalogAPI.getCategories().then(res => setCategories(res.data.categories)).catch(() => {});
  }, []);

  return (
    <div>
      <h1 className="text-lg font-semibold tracking-wider uppercase mb-4">Categories</h1>
      <div className="bg-white rounded-xl border border-[#e7e3dd] overflow-hidden">
        {categories.map(cat => (
          <div key={cat._id} className="px-4 py-3 border-b border-[#e7e3dd] last:border-0">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium">{cat.name}</p>
                <p className="text-xs text-[#6f6a63]">{cat.product_count} products | {cat.subcategories?.length || 0} subcategories</p>
              </div>
            </div>
            {cat.subcategories?.length > 0 && (
              <div className="mt-2 pl-4 flex flex-wrap gap-1">
                {cat.subcategories.map(sub => (
                  <span key={sub._id} className="text-xs bg-gray-100 px-2 py-0.5 rounded">{sub.name}</span>
                ))}
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}

function PermissionCheckboxes({ catalog, selected, onChange, disabled = false }) {
  const groups = catalog.reduce((result, permission) => {
    (result[permission.group] ||= []).push(permission);
    return result;
  }, {});
  const selectedSet = new Set(selected || []);
  const toggle = (key) => onChange(selectedSet.has(key) ? selected.filter(item => item !== key) : [...selected, key]);
  return (
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
      {Object.entries(groups).map(([group, permissions]) => (
        <fieldset key={group} className="border border-[#e7e3dd] rounded-lg p-3">
          <legend className="px-1 text-xs font-semibold text-[#2d2d2d]">{group}</legend>
          <div className="space-y-2">
            {permissions.map(permission => (
              <label key={permission.key} className="flex items-start gap-2 text-xs">
                <input type="checkbox" className="mt-0.5" checked={selectedSet.has(permission.key)} disabled={disabled} onChange={() => toggle(permission.key)} />
                <span><span className="font-medium block">{permission.label}</span><span className="text-[#6f6a63]">{permission.description}</span></span>
              </label>
            ))}
          </div>
        </fieldset>
      ))}
    </div>
  );
}

function AdminRoles() {
  const { user } = useAuth();
  const canManage = (user?.permissions || []).includes('roles.manage');
  const [roles, setRoles] = useState([]);
  const [catalog, setCatalog] = useState([]);
  const [editing, setEditing] = useState(null);
  const empty = { name: '', description: '', permissions: [], is_active: true };
  const [form, setForm] = useState(empty);

  const load = useCallback(async () => {
    try {
      const [rolesRes, catalogRes] = await Promise.all([adminAPI.getRoles(), adminAPI.getPermissionCatalog()]);
      setRoles(rolesRes.data.roles);
      setCatalog(catalogRes.data.permissions);
    } catch (error) { toast.error(formatApiError(error, 'Failed to load roles')); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const startCreate = () => { setEditing({ id: null }); setForm(empty); };
  const startEdit = role => {
    setEditing(role);
    setForm({ name: role.name, description: role.description || '', permissions: role.permissions || [], is_active: role.is_active });
  };
  const save = async event => {
    event.preventDefault();
    if (!window.confirm(`Save role “${form.name}” and revoke current sessions for affected users?`)) return;
    try {
      if (editing.id) await adminAPI.updateRole(editing.id, form);
      else await adminAPI.createRole(form);
      toast.success(editing.id ? 'Role updated' : 'Role created');
      setEditing(null);
      await load();
    } catch (error) { toast.error(formatApiError(error, 'Failed to save role')); }
  };
  const remove = async role => {
    let replacement = null;
    if (role.user_count > 0) {
      const choices = roles.filter(item => item.id !== role.id && item.is_active).map(item => `${item.id}: ${item.name}`).join('\n');
      replacement = window.prompt(`This role has ${role.user_count} assigned user(s). Enter the replacement role ID:\n${choices}`);
      if (!replacement) return;
    }
    if (!window.confirm(`Delete role “${role.name}”?`)) return;
    try { await adminAPI.deleteRole(role.id, replacement); toast.success('Role deleted'); await load(); }
    catch (error) { toast.error(formatApiError(error, 'Failed to delete role')); }
  };

  return (
    <div>
      <div className="flex items-center justify-between mb-4">
        <div><h1 className="text-lg font-semibold tracking-wider uppercase">Roles</h1><p className="text-xs text-[#6f6a63]">Reusable administrative access profiles.</p></div>
        {canManage && <button onClick={startCreate} className="flex items-center gap-2 bg-[#2f6b4f] text-white px-4 py-2.5 rounded-lg text-sm"><Plus size={16} /> New Role</button>}
      </div>
      {editing && <form onSubmit={save} className="bg-white border border-[#e7e3dd] rounded-xl p-5 mb-5">
        <div className="flex justify-between gap-3 mb-4"><h2 className="font-semibold">{editing.id ? 'Edit role' : 'Create role'}</h2><button type="button" aria-label="Close role editor" onClick={() => setEditing(null)}><X size={18} /></button></div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mb-4">
          <label className="text-xs">Role name *<input required minLength={2} maxLength={80} value={form.name} onChange={e => setForm(value => ({ ...value, name: e.target.value }))} className="mt-1 w-full px-3 py-2 border rounded-lg text-sm" /></label>
          <label className="text-xs">Description<input maxLength={500} value={form.description} onChange={e => setForm(value => ({ ...value, description: e.target.value }))} className="mt-1 w-full px-3 py-2 border rounded-lg text-sm" /></label>
        </div>
        <PermissionCheckboxes catalog={catalog} selected={form.permissions} onChange={permissions => setForm(value => ({ ...value, permissions }))} disabled={editing.is_protected} />
        {!editing.is_protected && editing.id && <label className="flex items-center gap-2 text-sm mt-4"><input type="checkbox" checked={form.is_active} onChange={e => setForm(value => ({ ...value, is_active: e.target.checked }))} /> Role active</label>}
        <div className="flex justify-end gap-2 mt-4"><button type="button" onClick={() => setEditing(null)} className="px-4 py-2 border rounded-lg text-xs">Cancel</button><button type="submit" className="px-4 py-2 bg-[#2f6b4f] text-white rounded-lg text-xs">Save role</button></div>
      </form>}
      <div className="grid gap-3">
        {roles.map(role => <article key={role.id} className="bg-white border border-[#e7e3dd] rounded-xl p-4">
          <div className="flex justify-between gap-3"><div><h2 className="font-semibold">{role.name}{!role.is_active && <span className="ml-2 text-xs text-red-600">Inactive</span>}</h2><p className="text-xs text-[#6f6a63] mt-1">{role.description || 'No description'}</p><p className="text-xs mt-2">{role.user_count} assigned user(s) · {role.is_full_access ? 'All administrative permissions' : `${role.permissions.length} permissions`}</p></div>
          {canManage && !role.is_protected && <div className="flex gap-1"><button onClick={() => startEdit(role)} className="p-2" aria-label={`Edit ${role.name}`}><Edit size={15} /></button><button onClick={() => remove(role)} className="p-2 text-red-600" aria-label={`Delete ${role.name}`}><Trash2 size={15} /></button></div>}</div>
          {!role.is_full_access && <p className="text-[11px] text-[#6f6a63] mt-2">{role.permissions.map(key => catalog.find(item => item.key === key)?.label || key).join(' · ') || 'No administrative actions'}</p>}
        </article>)}
      </div>
    </div>
  );
}

function AdminAudit() {
  const [events, setEvents] = useState([]);
  const [error, setError] = useState('');
  useEffect(() => {
    adminAPI.getRoleAudit({ limit: 100 }).then(response => setEvents(response.data.events)).catch(err => setError(formatApiError(err, 'Failed to load administrative records')));
  }, []);
  return <div>
    <div className="mb-4"><h1 className="text-lg font-semibold tracking-wider uppercase">Administrative records</h1><p className="text-xs text-[#6f6a63]">Role, permission, assignment and account-status changes.</p></div>
    {error && <p role="alert" className="text-sm text-red-700 mb-3">{error}</p>}
    <div className="bg-white rounded-xl border border-[#e7e3dd] overflow-x-auto">
      <table className="w-full text-sm"><thead className="bg-[#fbfaf8]"><tr><th className="text-left px-4 py-3 text-xs">When</th><th className="text-left px-4 py-3 text-xs">Action</th><th className="text-left px-4 py-3 text-xs">Performed by</th><th className="text-left px-4 py-3 text-xs">Affected users</th></tr></thead>
      <tbody>{events.map(event => <tr key={event.id} className="border-t border-[#e7e3dd]"><td className="px-4 py-3 text-xs whitespace-nowrap">{new Date(event.created_at).toLocaleString()}</td><td className="px-4 py-3 text-xs">{event.action}</td><td className="px-4 py-3 text-xs">{event.actor_email || 'System migration'}</td><td className="px-4 py-3 text-xs">{event.affected_user_ids?.length || 0}</td></tr>)}{!events.length && !error && <tr><td colSpan="4" className="px-4 py-8 text-center text-xs text-[#6f6a63]">No administrative changes recorded</td></tr>}</tbody></table>
    </div>
  </div>;
}

function AdminUserForm({
  isEdit,
  form,
  setForm,
  saving,
  canManageUsers,
  canManageRoles,
  roles,
  permissionCatalog,
  onSubmit,
  onClose,
}) {
  return (
    <form onSubmit={onSubmit} className="bg-white border border-[#e7e3dd] rounded-xl p-5 mb-4">
      <div className="flex items-center justify-between mb-4">
        <h2 className="text-sm font-semibold uppercase tracking-wider">{isEdit ? 'Edit User' : 'Create User'}</h2>
        <button type="button" aria-label="Close user form" onClick={onClose} className="text-[#6f6a63] hover:text-[#2d2d2d]"><X size={18} /></button>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        <div>
          <label htmlFor="user-form-email" className="text-xs text-[#6f6a63] block mb-1">Email *</label>
          <input id="user-form-email" data-testid="user-form-email" type="email" autoComplete="email" required value={form.email} onChange={e => setForm(f => ({ ...f, email: e.target.value }))} className="w-full px-3 py-2 border border-[#e7e3dd] rounded-lg text-sm" />
        </div>
        {canManageUsers && <div>
          <label htmlFor="user-form-password" className="text-xs text-[#6f6a63] block mb-1">{isEdit ? 'New Password (leave empty to keep)' : 'Password *'}</label>
          <input id="user-form-password" data-testid="user-form-password" type="password" autoComplete="new-password" required={!isEdit} minLength={12} maxLength={72} value={form.password} onChange={e => setForm(f => ({ ...f, password: e.target.value }))} placeholder={isEdit ? 'Leave blank to keep current password' : 'At least 12 characters'} className="w-full px-3 py-2 border border-[#e7e3dd] rounded-lg text-sm" />
        </div>}
        <div>
          <label htmlFor="user-form-name" className="text-xs text-[#6f6a63] block mb-1">Buyer Name</label>
          <input id="user-form-name" data-testid="user-form-name" autoComplete="name" value={form.buyer_name} onChange={e => setForm(f => ({ ...f, buyer_name: e.target.value }))} className="w-full px-3 py-2 border border-[#e7e3dd] rounded-lg text-sm" />
        </div>
        <div>
          <label htmlFor="user-form-company" className="text-xs text-[#6f6a63] block mb-1">Company</label>
          <input id="user-form-company" data-testid="user-form-company" autoComplete="organization" value={form.company_name} onChange={e => setForm(f => ({ ...f, company_name: e.target.value }))} className="w-full px-3 py-2 border border-[#e7e3dd] rounded-lg text-sm" />
        </div>
        <div>
          <label htmlFor="user-form-phone" className="text-xs text-[#6f6a63] block mb-1">Phone</label>
          <input id="user-form-phone" data-testid="user-form-phone" autoComplete="tel" value={form.phone} onChange={e => setForm(f => ({ ...f, phone: e.target.value }))} className="w-full px-3 py-2 border border-[#e7e3dd] rounded-lg text-sm" />
        </div>
        {!isEdit && canManageUsers && (
          <div>
            <label htmlFor="user-form-role" className="text-xs text-[#6f6a63] block mb-1">Role</label>
            <select id="user-form-role" data-testid="user-form-role" value={form.role_selection} onChange={e => setForm(f => ({ ...f, role_selection: e.target.value }))} className="w-full px-3 py-2 border border-[#e7e3dd] rounded-lg text-sm bg-white">
              <option value="customer">Customer</option>
              {roles.map(role => <option key={role.id} value={role.id}>{role.name}</option>)}
              {canManageRoles && <option value="new">Create new role…</option>}
            </select>
          </div>
        )}
      </div>
      {!isEdit && form.role_selection === 'new' && (
        <fieldset className="mt-4 border border-[#e7e3dd] rounded-lg p-4">
          <legend className="px-1 text-xs font-semibold">New reusable role</legend>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mb-4">
            <label className="text-xs">Role name *<input required value={form.new_role_name} onChange={e => setForm(f => ({ ...f, new_role_name: e.target.value }))} className="mt-1 w-full px-3 py-2 border rounded-lg text-sm" /></label>
            <label className="text-xs">Description<input value={form.new_role_description} onChange={e => setForm(f => ({ ...f, new_role_description: e.target.value }))} className="mt-1 w-full px-3 py-2 border rounded-lg text-sm" /></label>
          </div>
          <PermissionCheckboxes catalog={permissionCatalog} selected={form.new_role_permissions} onChange={value => setForm(f => ({ ...f, new_role_permissions: value }))} />
        </fieldset>
      )}
      <div className="flex justify-end gap-2 mt-4">
        <button type="button" onClick={onClose} className="px-4 py-2 text-xs border border-[#e7e3dd] rounded-lg hover:bg-gray-50">Cancel</button>
        <button type="submit" data-testid="user-form-submit" disabled={saving} className="px-4 py-2 text-xs bg-[#2f6b4f] text-white rounded-lg hover:bg-[#265a42] disabled:opacity-50">
          {saving ? 'Saving...' : (isEdit ? 'Update User' : 'Create User')}
        </button>
      </div>
    </form>
  );
}

// Customers
function AdminCustomers() {
  const { user } = useAuth();
  const permissions = new Set(user?.permissions || []);
  const canManageUsers = permissions.has('users.manage');
  const canEditCustomers = permissions.has('customers.edit');
  const canApprove = permissions.has('customers.approve');
  const canManageRoles = permissions.has('roles.manage');
  const canViewRoles = permissions.has('roles.view');
  const [customers, setCustomers] = useState([]);
  const [roles, setRoles] = useState([]);
  const [permissionCatalog, setPermissionCatalog] = useState([]);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [pendingCount, setPendingCount] = useState(0);
  const [showCreate, setShowCreate] = useState(false);
  const [editing, setEditing] = useState(null);
  const emptyForm = { email: '', password: '', buyer_name: '', company_name: '', phone: '', role_selection: 'customer', new_role_name: '', new_role_description: '', new_role_permissions: [] };
  const [form, setForm] = useState(emptyForm);
  const [saving, setSaving] = useState(false);

  const loadCustomers = useCallback(() => {
    adminAPI.getCustomers({ search, status: statusFilter }).then(res => {
      setCustomers(res.data.customers);
      setPendingCount(res.data.pending_count || 0);
    }).catch(() => {});
  }, [search, statusFilter]);

  useEffect(() => { loadCustomers(); }, [loadCustomers]);

  useEffect(() => {
    if (!canManageUsers || !canViewRoles) return;
    Promise.all([adminAPI.getRoles(), adminAPI.getPermissionCatalog()]).then(([roleRes, permissionRes]) => {
      setRoles(roleRes.data.roles.filter(role => role.is_active));
      setPermissionCatalog(permissionRes.data.permissions);
    }).catch(() => {});
  }, [canManageUsers, canViewRoles]); // permission changes force a new session and reload

  const resetForm = () => setForm(emptyForm);

  const handleCreate = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      let adminRoleId = form.role_selection === 'customer' ? null : form.role_selection;
      if (form.role_selection === 'new') {
        const roleRes = await adminAPI.createRole({
          name: form.new_role_name,
          description: form.new_role_description,
          permissions: form.new_role_permissions,
        });
        adminRoleId = roleRes.data.role.id;
      }
      await adminAPI.createUser({
        email: form.email, password: form.password, buyer_name: form.buyer_name,
        company_name: form.company_name, phone: form.phone, admin_role_id: adminRoleId,
      });
      toast.success('User created');
      setShowCreate(false);
      resetForm();
      loadCustomers();
    } catch (err) { toast.error(formatApiError(err, 'Error creating user')); }
    finally { setSaving(false); }
  };

  const handleUpdate = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const data = { buyer_name: form.buyer_name, company_name: form.company_name, phone: form.phone, email: form.email };
      if (form.password && canManageUsers) data.password = form.password;
      if (canManageUsers) await adminAPI.updateUser(editing._id, data);
      else await adminAPI.updateCustomer(editing._id, data);
      toast.success('User updated');
      setEditing(null);
      resetForm();
      loadCustomers();
    } catch (err) { toast.error(formatApiError(err, 'Error updating user')); }
    finally { setSaving(false); }
  };

  const handleApprove = async (id, approved) => {
    try {
      await adminAPI.approveCustomer(id, approved);
      toast.success(approved ? 'Customer approved' : 'Approval revoked');
      loadCustomers();
    } catch { toast.error('Action failed'); }
  };

  const handleAssignRole = async (target, roleId) => {
    const role = roles.find(item => item.id === roleId);
    const label = role?.name || 'Customer';
    if (!window.confirm(`Assign ${label} to ${target.email}? Their current sessions will be revoked.`)) return;
    try {
      await adminAPI.assignAdminRole(target._id, roleId || null);
      toast.success(`Role changed to ${label}`);
      loadCustomers();
    } catch (err) { toast.error(formatApiError(err, 'Failed')); }
  };

  const handleDelete = async (user) => {
    if (!window.confirm(`Delete user ${user.email}? This cannot be undone.`)) return;
    try {
      await adminAPI.deleteUser(user._id);
      toast.success('User deleted');
      loadCustomers();
    } catch (err) { toast.error(formatApiError(err, 'Cannot delete this user')); }
  };

  const handleActive = async (target, active) => {
    if (!window.confirm(`${active ? 'Activate' : 'Deactivate'} ${target.email}? Current sessions will be revoked.`)) return;
    try {
      await adminAPI.setUserActive(target._id, active);
      toast.success(active ? 'User activated' : 'User deactivated');
      loadCustomers();
    } catch (err) { toast.error(formatApiError(err, 'Cannot change account status')); }
  };

  const startEdit = (c) => {
    setEditing(c);
    setShowCreate(false);
    setForm({ ...emptyForm, email: c.email, password: '', buyer_name: c.buyer_name || '', company_name: c.company_name || '', phone: c.phone || '', role_selection: c.admin_role?.role_id || 'customer' });
  };

  return (
    <div>
      <div className="flex items-center justify-between mb-4">
        <div>
          <h1 className="text-lg font-semibold tracking-wider uppercase" data-testid="users-title">Users ({customers.length})</h1>
          {pendingCount > 0 && (
            <span className="text-xs text-orange-600 mt-1 inline-block">{pendingCount} pending approval</span>
          )}
        </div>
        {canManageUsers && <button data-testid="create-user-btn" onClick={() => { setShowCreate(true); setEditing(null); resetForm(); }} className="flex items-center gap-2 bg-[#2f6b4f] text-white px-4 py-2.5 rounded-lg text-sm hover:bg-[#265a42]">
          <Plus size={16} /> New User
        </button>}
      </div>

      {showCreate && <AdminUserForm
        isEdit={false}
        form={form}
        setForm={setForm}
        saving={saving}
        canManageUsers={canManageUsers}
        canManageRoles={canManageRoles}
        roles={roles}
        permissionCatalog={permissionCatalog}
        onSubmit={handleCreate}
        onClose={() => { setShowCreate(false); resetForm(); }}
      />}
      {editing && <AdminUserForm
        isEdit
        form={form}
        setForm={setForm}
        saving={saving}
        canManageUsers={canManageUsers}
        canManageRoles={canManageRoles}
        roles={roles}
        permissionCatalog={permissionCatalog}
        onSubmit={handleUpdate}
        onClose={() => { setEditing(null); resetForm(); }}
      />}

      <div className="flex gap-3 mb-4">
        <div className="relative flex-1">
          <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
          <label htmlFor="users-search" className="sr-only">Search users</label>
          <input id="users-search" type="text" value={search} onChange={e => setSearch(e.target.value)} placeholder="Search users..." className="w-full pl-9 pr-4 py-2 border border-[#e7e3dd] rounded-lg text-sm bg-white" data-testid="users-search" />
        </div>
        <label htmlFor="users-filter" className="sr-only">Filter users by status</label>
        <select id="users-filter" value={statusFilter} onChange={e => setStatusFilter(e.target.value)} className="px-3 py-2 border border-[#e7e3dd] rounded-lg text-sm bg-white" data-testid="users-filter">
          <option value="">All Users</option>
          <option value="pending">Pending Approval</option>
          <option value="approved">Approved</option>
          {permissions.has('users.view') && <option value="admin">Admins</option>}
          <option value="customer">Customers Only</option>
        </select>
      </div>

      <div className="bg-white rounded-xl border border-[#e7e3dd] overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-[#fbfaf8]">
            <tr>
              <th className="text-left px-4 py-3 text-xs uppercase tracking-wider text-[#6f6a63]">User</th>
              <th className="text-left px-4 py-3 text-xs uppercase tracking-wider text-[#6f6a63] hidden md:table-cell">Company</th>
              <th className="text-center px-4 py-3 text-xs uppercase tracking-wider text-[#6f6a63]">Role</th>
              <th className="text-center px-4 py-3 text-xs uppercase tracking-wider text-[#6f6a63]">Status</th>
              <th className="text-center px-4 py-3 text-xs uppercase tracking-wider text-[#6f6a63] hidden md:table-cell">Orders</th>
              <th className="text-right px-4 py-3 text-xs uppercase tracking-wider text-[#6f6a63]">Actions</th>
            </tr>
          </thead>
          <tbody>
            {customers.map(c => (
              <tr key={c._id} data-testid={`user-row-${c._id}`} className="border-t border-[#e7e3dd] hover:bg-gray-50">
                <td className="px-4 py-3">
                  <p className="text-xs font-medium">{c.buyer_name || c.email}</p>
                  <p className="text-[10px] text-[#6f6a63]">{c.email}</p>
                  {c.phone && <p className="text-[10px] text-[#6f6a63]">{c.phone}</p>}
                </td>
                <td className="px-4 py-3 text-xs text-[#6f6a63] hidden md:table-cell">{c.company_name || '-'}</td>
                <td className="px-4 py-3 text-center">
                  {canManageUsers ? <select
                    aria-label={`Role for ${c.email}`}
                    value={c.admin_role?.role_id || ''}
                    onChange={e => handleAssignRole(c, e.target.value)}
                    className="max-w-40 text-[10px] font-semibold px-2 py-1 rounded border border-[#e7e3dd] bg-white"
                  >
                    <option value="">Customer</option>
                    {roles.map(role => <option key={role.id} value={role.id}>{role.name}</option>)}
                  </select> : <span className="text-[10px] font-semibold px-2.5 py-1 rounded-full bg-[#f0efed] text-[#6f6a63]">{c.admin_role?.role_name || 'Customer'}</span>}
                </td>
                <td className="px-4 py-3 text-center">
                  {c.account_active === false ? (
                    <span className="text-[10px] font-medium px-2 py-0.5 rounded bg-red-50 text-red-700">Inactive</span>
                  ) : c.admin_access ? (
                    <span className="text-[10px] font-medium px-2 py-0.5 rounded bg-purple-50 text-purple-600">Admin</span>
                  ) : c.approved ? (
                    canApprove ? <button data-testid={`revoke-btn-${c._id}`} onClick={() => handleApprove(c._id, false)} className="text-[10px] font-medium px-2 py-0.5 rounded bg-green-50 text-green-700 hover:bg-green-100 cursor-pointer">
                      Approved ✓
                    </button> : <span className="text-[10px] text-green-700">Approved</span>
                  ) : (
                    canApprove ? <button data-testid={`approve-btn-${c._id}`} onClick={() => handleApprove(c._id, true)} className="text-[10px] font-medium px-2.5 py-1 rounded bg-orange-50 text-orange-700 hover:bg-orange-100 cursor-pointer">
                      Approve
                    </button> : <span className="text-[10px] text-orange-700">Pending</span>
                  )}
                </td>
                <td className="px-4 py-3 text-center text-xs hidden md:table-cell">{c.order_count}</td>
                <td className="px-4 py-3 text-right">
                  {(canManageUsers || canEditCustomers) && <div className="flex items-center justify-end gap-1">
                    {(canManageUsers || (canEditCustomers && !c.admin_access)) && <button data-testid={`edit-user-${c._id}`} onClick={() => startEdit(c)} className="p-1.5 hover:bg-blue-50 rounded" title="Edit">
                      <Edit size={14} className="text-blue-600" />
                    </button>}
                    {canManageUsers && <button data-testid={`delete-user-${c._id}`} onClick={() => handleDelete(c)} className="p-1.5 hover:bg-red-50 rounded" title="Delete">
                      <Trash2 size={14} className="text-red-500" />
                    </button>}
                    {canManageUsers && <button type="button" onClick={() => handleActive(c, c.account_active === false)} className="px-2 py-1 text-[10px] border rounded" title={c.account_active === false ? 'Activate account' : 'Deactivate account'}>{c.account_active === false ? 'Activate' : 'Deactivate'}</button>}
                  </div>}
                </td>
              </tr>
            ))}
            {customers.length === 0 && (
              <tr><td colSpan="6" className="px-4 py-8 text-center text-xs text-[#6f6a63]">No users found</td></tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}

// Discounts
function AdminDiscounts() {
  const [rules, setRules] = useState([]);
  const [coupons, setCoupons] = useState([]);
  const [newCoupon, setNewCoupon] = useState({ code: '', type: 'percentage', value: 0, min_purchase: 0 });

  useEffect(() => {
    adminAPI.getVolumeRules().then(res => setRules(res.data.rules)).catch(() => {});
    adminAPI.getCoupons().then(res => setCoupons(res.data.coupons)).catch(() => {});
  }, []);

  const handleCreateCoupon = async (e) => {
    e.preventDefault();
    try {
      await adminAPI.createCoupon(newCoupon);
      toast.success('Coupon created');
      setNewCoupon({ code: '', type: 'percentage', value: 0, min_purchase: 0 });
      adminAPI.getCoupons().then(res => setCoupons(res.data.coupons));
    } catch { toast.error('Failed'); }
  };

  return (
    <div>
      <h1 className="text-lg font-semibold tracking-wider uppercase mb-4">Discounts</h1>
      
      {/* Volume Rules */}
      <div className="mb-8">
        <h2 className="text-sm font-semibold mb-3">Volume Discount Rules</h2>
        <div className="bg-white rounded-xl border border-[#e7e3dd] p-4">
          {rules.map(r => (
            <div key={r._id} className="flex items-center justify-between py-2 border-b last:border-0">
              <span className="text-sm">${r.min_subtotal}+ subtotal</span>
              <span className="text-sm font-medium text-[#2f6b4f]">{r.discount_percent}% off</span>
            </div>
          ))}
        </div>
      </div>

      {/* Coupons */}
      <h2 className="text-sm font-semibold mb-3">Coupons</h2>
      <form onSubmit={handleCreateCoupon} className="bg-white rounded-xl border border-[#e7e3dd] p-4 mb-4">
        <div className="grid sm:grid-cols-4 gap-3">
          <input placeholder="Code" value={newCoupon.code} onChange={e => setNewCoupon(c => ({ ...c, code: e.target.value }))} className="px-3 py-2 border border-[#e7e3dd] rounded-lg text-sm" required />
          <select value={newCoupon.type} onChange={e => setNewCoupon(c => ({ ...c, type: e.target.value }))} className="px-3 py-2 border border-[#e7e3dd] rounded-lg text-sm">
            <option value="percentage">Percentage</option>
            <option value="fixed_amount">Fixed Amount</option>
          </select>
          <input type="number" placeholder="Value" value={newCoupon.value || ''} onChange={e => setNewCoupon(c => ({ ...c, value: parseFloat(e.target.value) || 0 }))} className="px-3 py-2 border border-[#e7e3dd] rounded-lg text-sm" />
          <button type="submit" className="bg-[#2d2d2d] text-white rounded-lg text-sm">Create</button>
        </div>
      </form>
      <div className="bg-white rounded-xl border border-[#e7e3dd] overflow-hidden">
        {coupons.map(c => (
          <div key={c._id} className="flex items-center justify-between px-4 py-3 border-b last:border-0">
            <div>
              <span className="text-sm font-mono font-medium">{c.code}</span>
              <span className="text-xs text-[#6f6a63] ml-2">{c.type === 'percentage' ? `${c.value}% off` : `$${c.value} off`}</span>
              {c.min_purchase > 0 && <span className="text-xs text-[#6f6a63] ml-2">min ${c.min_purchase}</span>}
            </div>
            <button onClick={async () => { await adminAPI.deleteCoupon(c._id); adminAPI.getCoupons().then(res => setCoupons(res.data.coupons)); toast.success('Deleted'); }} className="text-red-500"><Trash2 size={14} /></button>
          </div>
        ))}
      </div>
    </div>
  );
}

// Shop Now - Excel Upload & Catalog Management
function AdminSpecials() {
  const { user } = useAuth();
  const permissions = useMemo(() => new Set(user?.permissions || []), [user]);
  const canManage = permissions.has('specials.manage');
  const canImport = permissions.has('specials.import');
  const [stats, setStats] = useState(null);
  const [items, setItems] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [uploadResult, setUploadResult] = useState(null);
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [total, setTotal] = useState(0);
  const fileInputRef = React.useRef(null);
  const [dragOver, setDragOver] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [showCreateForm, setShowCreateForm] = useState(false);

  const loadStats = async () => {
    try {
      const res = await adminAPI.getSpecialsCatalogStats();
      setStats(res.data);
    } catch { setStats({ total_products: 0, total_categories: 0, categories: [], last_upload: null }); }
  };

  const loadItems = useCallback(async () => {
    try {
      const params = { page, limit: 20 };
      if (search) params.search = search;
      const res = await adminAPI.getSpecialsCatalogItems(params);
      setItems(res.data.items);
      setTotal(res.data.total);
      setTotalPages(res.data.pages);
    } catch { setItems([]); }
  }, [page, search]);

  useEffect(() => { loadStats(); }, []);
  useEffect(() => { loadItems(); }, [loadItems]);

  const handleUpload = async (file) => {
    if (!file) return;
    if (!file.name.match(/\.xlsx$/i)) {
      toast.error('Only .xlsx files are supported');
      return;
    }
    setUploading(true);
    setUploadResult(null);
    try {
      const res = await adminAPI.uploadSpecialsExcel(file);
      setUploadResult(res.data);
      toast.success(res.data.message);
      setPage(1);
      loadStats();
      loadItems();
    } catch (err) {
      const detail = formatApiError(err, 'Upload failed');
      toast.error(detail);
      setUploadResult({ success: false, message: detail });
    } finally {
      setUploading(false);
    }
  };

  const handleFileDrop = (e) => {
    e.preventDefault();
    setDragOver(false);
    const file = e.dataTransfer.files?.[0];
    if (file) handleUpload(file);
  };

  const handleSaveItem = async (data) => {
    try {
      if (editingItem) {
        await adminAPI.updateSpecialsItem(editingItem._id, data);
        toast.success('Product updated');
      } else {
        await adminAPI.createSpecialsItem(data);
        toast.success('Product created');
      }
      setEditingItem(null);
      setShowCreateForm(false);
      loadStats();
      loadItems();
    } catch (err) {
      toast.error(formatApiError(err, 'Failed to save'));
    }
  };

  const handleDeleteItem = async (id) => {
    if (!window.confirm('Delete this product?')) return;
    try {
      await adminAPI.deleteSpecialsItem(id);
      toast.success('Product deleted');
      loadStats();
      loadItems();
    } catch { toast.error('Failed to delete'); }
  };

  const formatDate = (iso) => {
    if (!iso) return 'Never';
    const d = new Date(iso);
    return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric', hour: '2-digit', minute: '2-digit' });
  };

  return (
    <div data-testid="admin-specials-section">
      <div className="flex items-center justify-between mb-2">
        <h1 className="text-lg font-semibold tracking-wider uppercase">Shop Now</h1>
        {stats?.total_products > 0 && (
          <span className="text-xs bg-[#e7f1ea] text-[#2f6b4f] px-3 py-1 rounded-full font-medium">
            {stats.total_products} products loaded
          </span>
        )}
      </div>
      <p className="text-sm text-[#6f6a63] mb-6">
        Quick-sale products with dispatch within <strong>48 hours</strong>. Upload an Excel file or add products individually. This catalog is independent from "Our Varieties / Prebook Orders" (main inventory).
      </p>

      {/* Stats Cards */}
      {stats && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6">
          <div className="bg-white rounded-xl border border-[#e7e3dd] p-4">
            <p className="text-[10px] uppercase tracking-wider text-[#6f6a63]">Total Products</p>
            <p className="text-2xl font-semibold mt-1">{stats.total_products}</p>
          </div>
          <div className="bg-white rounded-xl border border-[#e7e3dd] p-4">
            <p className="text-[10px] uppercase tracking-wider text-[#6f6a63]">Categories</p>
            <p className="text-2xl font-semibold mt-1">{stats.total_categories}</p>
          </div>
          <div className="bg-white rounded-xl border border-[#e7e3dd] p-4">
            <p className="text-[10px] uppercase tracking-wider text-[#6f6a63]">Dispatch</p>
            <p className="text-lg font-semibold mt-1 text-[#2f6b4f]">48h</p>
          </div>
          <div className="bg-white rounded-xl border border-[#e7e3dd] p-4">
            <p className="text-[10px] uppercase tracking-wider text-[#6f6a63]">Last Upload</p>
            <p className="text-xs font-medium mt-2">{formatDate(stats.last_upload)}</p>
          </div>
        </div>
      )}

      {/* Excel Upload Section */}
      {canImport && <div className="bg-white rounded-xl border border-[#e7e3dd] p-6 mb-6">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-sm font-semibold">Bulk Upload (Excel)</h2>
        </div>

        {/* Dropzone */}
        <div
          data-testid="admin-specials-excel-dropzone"
          onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
          onDragLeave={() => setDragOver(false)}
          onDrop={handleFileDrop}
          onClick={() => fileInputRef.current?.click()}
          className={`border-2 border-dashed rounded-xl p-8 text-center cursor-pointer transition-colors ${
            dragOver ? 'border-[#2f6b4f] bg-[#e7f1ea]/30' : 'border-[#d5d0c8] hover:border-[#2f6b4f] hover:bg-[#fbfaf8]'
          } ${uploading ? 'pointer-events-none opacity-60' : ''}`}
        >
          <input
            ref={fileInputRef}
            type="file"
            accept=".xlsx"
            className="hidden"
            onChange={(e) => handleUpload(e.target.files?.[0])}
          />
          {uploading ? (
            <div className="flex flex-col items-center gap-3">
              <div className="w-8 h-8 border-2 border-[#2f6b4f] border-t-transparent rounded-full animate-spin" />
              <p className="text-sm text-[#6f6a63]">Processing Excel file...</p>
            </div>
          ) : (
            <div className="flex flex-col items-center gap-3">
              <Upload size={32} className="text-[#6f6a63]" />
              <div>
                <p className="text-sm text-[#2d2d2d]">
                  <span className="text-[#2f6b4f] font-semibold">Click to upload</span> or drag and drop
                </p>
                <p className="text-xs text-[#6f6a63] mt-1">Excel files (.xlsx) - Max 10MB</p>
              </div>
            </div>
          )}
        </div>

        {/* Upload Result */}
        {uploadResult && (
          <div className={`mt-4 p-3 rounded-lg text-sm ${uploadResult.success ? 'bg-[#e7f1ea] text-[#2f6b4f]' : 'bg-red-50 text-red-700'}`}>
            <p className="font-medium">{uploadResult.message}</p>
            {uploadResult.columns_detected && (
              <p className="text-xs mt-1 opacity-80">Columns detected: {uploadResult.columns_detected.join(', ')}</p>
            )}
          </div>
        )}

        {/* Expected Format */}
        <div className="mt-4 p-4 bg-[#fbfaf8] rounded-lg">
          <p className="text-xs font-semibold text-[#2d2d2d] mb-2">Expected Excel Format</p>
          <p className="text-[11px] text-[#6f6a63] mb-2">The first row must contain column headers. Supported columns:</p>
          <div className="flex flex-wrap gap-1.5">
            {['Name *', 'SKU *', 'Category', 'Color', 'Price', 'Stock', 'Pack Size', 'Image URL', 'Description'].map(col => (
              <span key={col} className={`text-[10px] px-2 py-0.5 rounded ${col.includes('*') ? 'bg-[#2f6b4f] text-white' : 'bg-white border border-[#e7e3dd] text-[#6f6a63]'}`}>
                {col}
              </span>
            ))}
          </div>
          <p className="text-[10px] text-[#999] mt-2">* Required. Existing products are matched by SKU. Blank cells and products not listed in the file remain unchanged.</p>
        </div>
      </div>}

      {/* Create / Edit Form */}
      {canManage && (showCreateForm || editingItem) && (
        <SpecialsProductForm
          item={editingItem}
          categories={stats?.categories || []}
          onSave={handleSaveItem}
          onCancel={() => { setEditingItem(null); setShowCreateForm(false); }}
        />
      )}

      {/* Products Table */}
      <div className="bg-white rounded-xl border border-[#e7e3dd] overflow-hidden">
        <div className="flex items-center justify-between px-4 py-3 border-b border-[#e7e3dd] bg-[#fbfaf8]">
          <h2 className="text-sm font-semibold">Current Catalog ({total} products)</h2>
          <div className="flex items-center gap-3">
            <div className="relative">
              <Search size={13} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-gray-400" />
              <input
                data-testid="admin-specials-search"
                type="text"
                value={search}
                onChange={(e) => { setSearch(e.target.value); setPage(1); }}
                placeholder="Search..."
                className="pl-8 pr-3 py-1.5 border border-[#e7e3dd] rounded-lg text-xs bg-white focus:outline-none w-48"
              />
            </div>
            {canManage && <button
              data-testid="admin-specials-add-btn"
              onClick={() => { setEditingItem(null); setShowCreateForm(true); }}
              className="flex items-center gap-1 bg-[#2d2d2d] text-white px-3 py-1.5 rounded-lg text-xs hover:bg-[#232323] transition-colors"
            >
              <Plus size={13} /> Add Product
            </button>}
          </div>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-[#fbfaf8]">
              <tr>
                <th className="text-left px-4 py-2.5 text-[10px] uppercase tracking-wider text-[#6f6a63]">Product</th>
                <th className="text-left px-4 py-2.5 text-[10px] uppercase tracking-wider text-[#6f6a63]">Category</th>
                <th className="text-left px-4 py-2.5 text-[10px] uppercase tracking-wider text-[#6f6a63]">Color</th>
                <th className="text-center px-4 py-2.5 text-[10px] uppercase tracking-wider text-[#6f6a63]">Price</th>
                <th className="text-center px-4 py-2.5 text-[10px] uppercase tracking-wider text-[#6f6a63]">Stock</th>
                <th className="text-left px-4 py-2.5 text-[10px] uppercase tracking-wider text-[#6f6a63]">Pack Size</th>
                {canManage && <th className="text-right px-4 py-2.5 text-[10px] uppercase tracking-wider text-[#6f6a63]">Actions</th>}
              </tr>
            </thead>
            <tbody>
              {items.map(item => (
                <tr key={item._id} className="border-t border-[#e7e3dd] hover:bg-gray-50 transition-colors">
                  <td className="px-4 py-2.5">
                    <div className="flex items-center gap-2">
                      {item.image_url ? (
                        <img src={item.image_url} alt={item.name || 'Shop Now product'} className="w-8 h-8 rounded object-cover flex-shrink-0" />
                      ) : (
                        <div className="w-8 h-8 rounded bg-[#f5f4f2] flex items-center justify-center flex-shrink-0">
                          <ImageIcon size={12} className="text-[#c0c0c0]" />
                        </div>
                      )}
                      <div>
                        <p className="text-xs font-medium">{item.name}</p>
                        {item.sku && <p className="text-[10px] text-[#6f6a63]">{item.sku}</p>}
                      </div>
                    </div>
                  </td>
                  <td className="px-4 py-2.5 text-xs text-[#6f6a63]">{item.category || '-'}</td>
                  <td className="px-4 py-2.5 text-xs text-[#6f6a63]">{item.color || '-'}</td>
                  <td className="px-4 py-2.5 text-center text-xs font-medium">
                    {item.price > 0 ? `$${item.price.toFixed(2)}` : '-'}
                  </td>
                  <td className="px-4 py-2.5 text-center">
                    {item.stock > 0 ? (
                      <span className={`text-xs px-2 py-0.5 rounded ${item.stock <= 5 ? 'bg-amber-50 text-amber-600' : 'bg-green-50 text-green-600'}`}>
                        {item.stock}
                      </span>
                    ) : (
                      <span className="text-xs text-[#c0c0c0]">-</span>
                    )}
                  </td>
                  <td className="px-4 py-2.5 text-xs text-[#6f6a63]">{item.pack_size || '-'}</td>
                  {canManage && <td className="px-4 py-2.5 text-right">
                    {canManage && <button
                      data-testid={`specials-edit-${item._id}`}
                      onClick={() => { setEditingItem(item); setShowCreateForm(false); }}
                      className="text-blue-600 hover:text-blue-800 mr-2 transition-colors"
                      title="Edit"
                    >
                      <Edit size={14} />
                    </button>}
                    {canManage && <button
                      data-testid={`specials-delete-${item._id}`}
                      onClick={() => handleDeleteItem(item._id)}
                      className="text-red-500 hover:text-red-700 transition-colors"
                      title="Delete"
                    >
                      <Trash2 size={14} />
                    </button>}
                  </td>}
                </tr>
              ))}
              {items.length === 0 && (
                <tr>
                  <td colSpan={canManage ? 7 : 6} className="px-4 py-8 text-center text-xs text-[#6f6a63]">
                    {search ? 'No products match your search' : 'No products in catalog. Upload an Excel or add products individually.'}
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
        {/* Pagination */}
        {totalPages > 1 && (
          <div className="flex items-center justify-center gap-1.5 py-3 border-t border-[#e7e3dd]">
            {Array.from({ length: totalPages }, (_, i) => i + 1).slice(0, 10).map(p => (
              <button
                key={p}
                onClick={() => setPage(p)}
                className={`w-7 h-7 rounded text-xs transition-colors ${page === p ? 'bg-[#2d2d2d] text-white' : 'border border-[#e7e3dd] hover:bg-gray-50'}`}
              >{p}</button>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

// Form for creating/editing a single Shop Now product
function SpecialsProductForm({ item, categories, onSave, onCancel }) {
  const [form, setForm] = useState({
    name: '', sku: '', category: '', color: '', description: '',
    price: 0, stock: 0, pack_size: '', image_url: ''
  });
  const [uploading, setUploading] = useState(false);
  const [pendingImageSave, setPendingImageSave] = useState(false);
  const [dragOver, setDragOver] = useState(false);
  const imgInputRef = React.useRef(null);

  useEffect(() => {
    if (item) {
      setForm({
        name: item.name || '',
        sku: item.sku || '',
        category: item.category || '',
        color: item.color || '',
        description: item.description || '',
        price: item.price || 0,
        stock: item.stock || 0,
        pack_size: item.pack_size || '',
        image_url: item.image_url || ''
      });
    }
  }, [item]);

  const handleSubmit = (e) => {
    e.preventDefault();
    onSave(form);
  };

  const handleImageUpload = async (file) => {
    if (!file || !file.type.startsWith('image/')) {
      toast.error('Only image files are allowed');
      return;
    }
    if (file.size > 5 * 1024 * 1024) {
      toast.error('Max file size is 5MB');
      return;
    }
    setUploading(true);
    try {
      const res = await adminAPI.uploadImage(file);
      setForm(f => ({ ...f, image_url: res.data.url }));
      setPendingImageSave(true);
      toast.success('Image uploaded. Click Save Changes to attach it to this product.');
    } catch {
      toast.error('Upload failed');
    } finally {
      setUploading(false);
    }
  };

  const handleImgDrop = (e) => {
    e.preventDefault();
    setDragOver(false);
    handleImageUpload(e.dataTransfer.files?.[0]);
  };

  const handleCancel = () => {
    if (uploading) return;
    if (pendingImageSave && !window.confirm('Uploaded image is not attached to a product until you save. Discard changes?')) return;
    onCancel();
  };

  const u = (key, val) => setForm(f => ({ ...f, [key]: val }));

  return (
    <div data-testid="specials-product-form" className="bg-white border border-[#e7e3dd] rounded-xl p-6 mb-6">
      <div className="flex justify-between items-center mb-4">
        <h3 className="text-sm font-semibold">{item ? 'Edit Product' : 'Add New Product'}</h3>
        <button onClick={handleCancel} disabled={uploading} className="p-1 hover:bg-[#fbfaf8] rounded-lg transition-colors"><X size={18} className="text-[#6f6a63]" /></button>
      </div>
      <form onSubmit={handleSubmit} className="space-y-4">
        {/* Row 1: Name, SKU */}
        <div className="grid sm:grid-cols-2 gap-4">
          <div>
            <label className="text-xs text-[#6f6a63] block mb-1">Name *</label>
            <input data-testid="specials-form-name" required value={form.name} onChange={e => u('name', e.target.value)} className="w-full px-3 py-2 border border-[#e7e3dd] rounded-lg text-sm focus:outline-none focus:border-[#2f6b4f]" />
          </div>
          <div>
            <label className="text-xs text-[#6f6a63] block mb-1">SKU</label>
            <input data-testid="specials-form-sku" value={form.sku} onChange={e => u('sku', e.target.value)} className="w-full px-3 py-2 border border-[#e7e3dd] rounded-lg text-sm focus:outline-none focus:border-[#2f6b4f]" />
          </div>
        </div>

        {/* Row 2: Category, Color */}
        <div className="grid sm:grid-cols-2 gap-4">
          <div>
            <label className="text-xs text-[#6f6a63] block mb-1">Category</label>
            <input
              data-testid="specials-form-category"
              value={form.category}
              onChange={e => u('category', e.target.value)}
              list="specials-category-list"
              className="w-full px-3 py-2 border border-[#e7e3dd] rounded-lg text-sm focus:outline-none focus:border-[#2f6b4f]"
              placeholder="Type or select..."
            />
            <datalist id="specials-category-list">
              {categories.map(c => <option key={c} value={c} />)}
            </datalist>
          </div>
          <div>
            <label className="text-xs text-[#6f6a63] block mb-1">Color</label>
            <ColorSelect value={form.color} onChange={v => u('color', v)} testId="specials-form-color" />
          </div>
        </div>

        {/* Row 3: Price, Stock, Pack Size */}
        <div className="grid sm:grid-cols-3 gap-4">
          <div>
            <label className="text-xs text-[#6f6a63] block mb-1">Price ($)</label>
            <input data-testid="specials-form-price" type="number" step="0.01" min="0" value={form.price || ''} onChange={e => u('price', parseFloat(e.target.value) || 0)} className="w-full px-3 py-2 border border-[#e7e3dd] rounded-lg text-sm focus:outline-none focus:border-[#2f6b4f]" />
          </div>
          <div>
            <label className="text-xs text-[#6f6a63] block mb-1">Stock</label>
            <input data-testid="specials-form-stock" type="number" min="0" value={form.stock || ''} onChange={e => u('stock', parseInt(e.target.value) || 0)} className="w-full px-3 py-2 border border-[#e7e3dd] rounded-lg text-sm focus:outline-none focus:border-[#2f6b4f]" />
          </div>
          <div>
            <label className="text-xs text-[#6f6a63] block mb-1">Pack Size</label>
            <input data-testid="specials-form-packsize" value={form.pack_size} onChange={e => u('pack_size', e.target.value)} placeholder="e.g. 25 stems/bunch" className="w-full px-3 py-2 border border-[#e7e3dd] rounded-lg text-sm focus:outline-none focus:border-[#2f6b4f]" />
          </div>
        </div>

        {/* Description */}
        <div>
          <label className="text-xs text-[#6f6a63] block mb-1">Description</label>
          <textarea data-testid="specials-form-description" rows={2} value={form.description} onChange={e => u('description', e.target.value)} className="w-full px-3 py-2 border border-[#e7e3dd] rounded-lg text-sm focus:outline-none focus:border-[#2f6b4f] resize-none" />
        </div>

        {/* Product Image */}
        <div>
          <label className="text-xs text-[#6f6a63] block mb-2">Product Image</label>
          <div className="flex items-start gap-4">
            {/* Current image preview */}
            {form.image_url && (
              <div className="relative w-24 h-24 rounded-lg overflow-hidden border border-[#e7e3dd] flex-shrink-0 group">
                <img src={form.image_url} alt="Preview" className="w-full h-full object-cover" />
                <button
                  type="button"
                  onClick={() => u('image_url', '')}
                  className="absolute inset-0 bg-black/40 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center"
                  title="Remove image"
                >
                  <Trash2 size={16} className="text-white" />
                </button>
              </div>
            )}
            {/* Upload area */}
            <div
              data-testid="specials-form-image-dropzone"
              onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
              onDragLeave={() => setDragOver(false)}
              onDrop={handleImgDrop}
              onClick={() => imgInputRef.current?.click()}
              className={`flex-1 border-2 border-dashed rounded-lg p-4 text-center cursor-pointer transition-colors ${
                dragOver ? 'border-[#2f6b4f] bg-[#e7f1ea]/30' : 'border-[#d5d0c8] hover:border-[#2f6b4f] hover:bg-[#fbfaf8]'
              } ${uploading ? 'pointer-events-none opacity-60' : ''}`}
            >
              <input
                ref={imgInputRef}
                type="file"
                accept="image/jpeg,image/png,image/webp,image/gif"
                className="hidden"
                onChange={(e) => handleImageUpload(e.target.files?.[0])}
              />
              {uploading ? (
                <div className="flex items-center justify-center gap-2">
                  <div className="w-5 h-5 border-2 border-[#2f6b4f] border-t-transparent rounded-full animate-spin" />
                  <span className="text-xs text-[#6f6a63]">Uploading...</span>
                </div>
              ) : (
                <div className="flex flex-col items-center gap-1">
                  <Upload size={20} className="text-[#6f6a63]" />
                  <p className="text-xs text-[#6f6a63]"><span className="text-[#2f6b4f] font-medium">Click</span> or drag image</p>
                  <p className="text-[10px] text-[#999]">JPEG, PNG, WebP (max 5MB)</p>
                </div>
              )}
            </div>
          </div>
          {/* Or paste URL */}
          <div className="mt-2">
            <label className="text-[10px] text-[#999] block mb-1">Or paste image URL</label>
            <input
              data-testid="specials-form-image-url"
              type="text"
              value={form.image_url}
              onChange={e => u('image_url', e.target.value)}
              placeholder="https://... or /api/uploads/..."
              className="w-full px-3 py-1.5 border border-[#e7e3dd] rounded-lg text-xs focus:outline-none focus:border-[#2f6b4f]"
            />
          </div>
        </div>

        {/* Buttons */}
        <div className="flex gap-3 pt-2">
          <button data-testid="specials-form-save" type="submit" disabled={uploading} className="bg-[#2d2d2d] text-white px-6 py-2 rounded-lg text-sm hover:bg-[#232323] transition-colors disabled:opacity-50">
            {item ? 'Save Changes' : 'Add Product'}
          </button>
          <button type="button" onClick={handleCancel} disabled={uploading} className="border border-[#e7e3dd] px-6 py-2 rounded-lg text-sm hover:bg-[#fbfaf8] transition-colors disabled:opacity-50">Cancel</button>
        </div>
      </form>
    </div>
  );
}

// Prebook
function AdminPrebook() {
  const [products, setProducts] = useState([]);
  useEffect(() => {
    adminAPI.getProducts({ limit: 100 }).then(res => setProducts(res.data.products)).catch(() => {});
  }, []);

  const togglePrebook = async (id, current) => {
    await adminAPI.togglePrebook(id, !current);
    setProducts(products.map(p => p._id === id ? { ...p, is_prebook_eligible: !current } : p));
    toast.success('Updated');
  };

  return (
    <div>
      <h1 className="text-lg font-semibold tracking-wider uppercase mb-4">Prebook Management</h1>
      <p className="text-sm text-[#6f6a63] mb-4">Toggle products eligible for Prebook Orders (Mondays only, 3+ weeks advance)</p>
      <div className="bg-white rounded-xl border border-[#e7e3dd] overflow-hidden">
        {products.map(p => (
          <div key={p._id} className="flex items-center justify-between px-4 py-2 border-b last:border-0">
            <div className="flex items-center gap-2">
              <img src={p.images?.[0]} alt="" className="w-8 h-8 rounded object-cover" />
              <span className="text-sm">{p.name}</span>
            </div>
            <button onClick={() => togglePrebook(p._id, p.is_prebook_eligible)}
              className={`text-xs px-3 py-1 rounded-full ${p.is_prebook_eligible ? 'bg-[#b28a4a]/10 text-[#b28a4a]' : 'bg-gray-100 text-gray-500'}`}>
              {p.is_prebook_eligible ? 'Prebook' : 'Not Prebook'}
            </button>
          </div>
        ))}
      </div>
    </div>
  );
}

// ── Admin Holidays ──
function AdminHolidays() {
  const [holidays, setHolidays] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [formDate, setFormDate] = useState('');
  const [formLabel, setFormLabel] = useState('');
  const [saving, setSaving] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const res = await adminAPI.getHolidays();
      setHolidays(res.data.holidays);
    } catch { /* ignore */ } finally { setLoading(false); }
  };

  useEffect(() => { load(); }, []);

  const openAdd = () => {
    setEditingId(null);
    setFormDate('');
    setFormLabel('');
    setShowForm(true);
  };

  const openEdit = (h) => {
    setEditingId(h._id);
    setFormDate(h.date);
    setFormLabel(h.label || '');
    setShowForm(true);
  };

  const handleSave = async () => {
    if (!formDate) { toast.error('Date is required'); return; }
    setSaving(true);
    try {
      if (editingId) {
        await adminAPI.updateHoliday(editingId, { date: formDate, label: formLabel });
        toast.success('Holiday updated');
      } else {
        await adminAPI.createHoliday({ date: formDate, label: formLabel });
        toast.success('Holiday added');
      }
      setShowForm(false);
      load();
    } catch (err) {
      toast.error(formatApiError(err, 'Failed to save'));
    } finally { setSaving(false); }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this holiday?')) return;
    try {
      await adminAPI.deleteHoliday(id);
      toast.success('Holiday deleted');
      load();
    } catch { toast.error('Failed to delete'); }
  };

  const formatDate = (dateStr) => {
    const d = new Date(dateStr + 'T12:00:00');
    return d.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric', year: 'numeric' });
  };

  const isPast = (dateStr) => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    return new Date(dateStr + 'T12:00:00') < today;
  };

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-xl font-semibold tracking-wider uppercase">Non-Working Days</h1>
          <p className="text-sm text-[#6f6a63] mt-1">Manage holidays and non-working days. These dates will be skipped when calculating delivery dates.</p>
        </div>
        <button data-testid="add-holiday-btn" onClick={openAdd} className="flex items-center gap-2 bg-[#2d2d2d] text-white px-4 py-2.5 rounded-lg text-sm hover:bg-[#232323]">
          <Plus size={16} /> Add Holiday
        </button>
      </div>

      {/* Form */}
      {showForm && (
        <div className="bg-white border border-[#e7e3dd] rounded-xl p-5 mb-6">
          <h3 className="text-sm font-semibold mb-4">{editingId ? 'Edit Holiday' : 'Add Holiday'}</h3>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className="text-xs font-medium text-[#6f6a63] block mb-1">Date *</label>
              <input data-testid="holiday-date-input" type="date" value={formDate} onChange={(e) => setFormDate(e.target.value)} className="w-full px-3 py-2 border border-[#e7e3dd] rounded-lg text-sm focus:outline-none focus:border-[#2f6b4f]" />
            </div>
            <div>
              <label className="text-xs font-medium text-[#6f6a63] block mb-1">Label (optional)</label>
              <input data-testid="holiday-label-input" type="text" value={formLabel} onChange={(e) => setFormLabel(e.target.value)} placeholder="e.g. Christmas, New Year..." className="w-full px-3 py-2 border border-[#e7e3dd] rounded-lg text-sm focus:outline-none focus:border-[#2f6b4f]" />
            </div>
          </div>
          <div className="flex gap-3 mt-4">
            <button data-testid="holiday-save-btn" onClick={handleSave} disabled={saving} className="flex items-center gap-2 bg-[#2f6b4f] text-white px-5 py-2 rounded-lg text-sm hover:bg-[#265a42] disabled:opacity-50">
              <Check size={14} /> {saving ? 'Saving...' : 'Save'}
            </button>
            <button onClick={() => setShowForm(false)} className="px-5 py-2 border border-[#e7e3dd] rounded-lg text-sm hover:bg-gray-50">Cancel</button>
          </div>
        </div>
      )}

      {/* Table */}
      {loading ? (
        <div className="space-y-3">{[...Array(4)].map((_, i) => <div key={i} className="animate-pulse bg-[#fbfaf8] rounded-lg h-12" />)}</div>
      ) : holidays.length === 0 ? (
        <div className="text-center py-12 bg-white border border-[#e7e3dd] rounded-xl">
          <CalendarOff size={36} className="mx-auto text-[#c0c0c0] mb-3" />
          <p className="text-[#6f6a63] text-sm">No holidays configured yet.</p>
          <p className="text-[#c0c0c0] text-xs mt-1">Add holidays to exclude them from delivery date calculations.</p>
        </div>
      ) : (
        <div className="bg-white border border-[#e7e3dd] rounded-xl overflow-hidden">
          <div className="hidden md:grid md:grid-cols-[200px_1fr_100px_120px] items-center bg-[#fbfaf8] border-b border-[#e7e3dd] px-4 py-2.5">
            <span className="text-[10px] font-semibold tracking-wider uppercase text-[#6f6a63]">Date</span>
            <span className="text-[10px] font-semibold tracking-wider uppercase text-[#6f6a63]">Label</span>
            <span className="text-[10px] font-semibold tracking-wider uppercase text-[#6f6a63] text-center">Status</span>
            <span className="text-[10px] font-semibold tracking-wider uppercase text-[#6f6a63] text-center">Actions</span>
          </div>
          {holidays.map((h, idx) => (
            <div key={h._id} data-testid={`holiday-row-${h._id}`} className={`grid grid-cols-1 md:grid-cols-[200px_1fr_100px_120px] items-center px-4 py-3 border-b border-[#f0efed] ${idx % 2 !== 0 ? 'bg-[#fafaf9]' : ''}`}>
              <span className="text-sm font-medium text-[#2d2d2d]">{formatDate(h.date)}</span>
              <span className="text-sm text-[#6f6a63]">{h.label || '-'}</span>
              <div className="text-center">
                {isPast(h.date) ? (
                  <span className="text-[10px] px-2 py-0.5 rounded-full bg-gray-100 text-gray-500">Past</span>
                ) : (
                  <span className="text-[10px] px-2 py-0.5 rounded-full bg-[#e7f1ea] text-[#2f6b4f]">Active</span>
                )}
              </div>
              <div className="flex justify-center gap-2">
                <button data-testid={`holiday-edit-${h._id}`} onClick={() => openEdit(h)} className="p-1.5 hover:bg-gray-100 rounded"><Edit size={14} className="text-[#6f6a63]" /></button>
                <button data-testid={`holiday-delete-${h._id}`} onClick={() => handleDelete(h._id)} className="p-1.5 hover:bg-red-50 rounded"><Trash2 size={14} className="text-red-500" /></button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}



function AdminSubscribers() {
  const [subscribers, setSubscribers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [total, setTotal] = useState(0);
  const fileInputRef = useRef(null);
  const [importing, setImporting] = useState(false);

  const load = useCallback(async (p = page, q = search) => {
    setLoading(true);
    try {
      const res = await adminAPI.getSubscribers({ page: p, search: q, limit: 50 });
      setSubscribers(res.data.subscribers);
      setTotalPages(res.data.pages);
      setTotal(res.data.total);
    } catch { /* ignore */ } finally { setLoading(false); }
  }, [page, search]);

  useEffect(() => { load(); }, [load]);

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this subscriber?')) return;
    try {
      await adminAPI.deleteSubscriber(id);
      toast.success('Subscriber deleted');
      load();
    } catch { toast.error('Failed to delete'); }
  };

  const handleExport = async () => {
    try {
      const res = await adminAPI.exportSubscribers();
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement('a');
      a.href = url;
      a.download = 'subscribers.csv';
      a.click();
      window.URL.revokeObjectURL(url);
      toast.success('CSV downloaded');
    } catch { toast.error('Export failed'); }
  };

  const handleImport = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setImporting(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      const res = await adminAPI.importSubscribers(formData);
      const d = res.data;
      toast.success(`Import done: ${d.added} added, ${d.skipped} skipped`);
      if (d.errors?.length) {
        d.errors.slice(0, 5).forEach(err => toast.error(err));
      }
      load(1, '');
      setSearch('');
      setPage(1);
    } catch { toast.error('Import failed'); } finally {
      setImporting(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  const formatDate = (dt) => {
    if (!dt) return '-';
    const d = new Date(dt);
    return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric', hour: '2-digit', minute: '2-digit' });
  };

  return (
    <div>
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3 mb-6">
        <div>
          <h1 className="text-xl font-semibold tracking-wider uppercase" data-testid="subscribers-title">Subscribers</h1>
          <p className="text-sm text-[#6f6a63] mt-1">Newsletter subscribers from the homepage. Total: <span data-testid="subscribers-total" className="font-medium text-[#2d2d2d]">{total}</span></p>
        </div>
        <div className="flex gap-2">
          <button data-testid="subscribers-export-btn" onClick={handleExport} className="flex items-center gap-2 bg-[#2d2d2d] text-white px-4 py-2.5 rounded-lg text-sm hover:bg-[#232323]">
            <Download size={16} /> Export CSV
          </button>
          <button data-testid="subscribers-import-btn" onClick={() => fileInputRef.current?.click()} disabled={importing} className="flex items-center gap-2 bg-[#2f6b4f] text-white px-4 py-2.5 rounded-lg text-sm hover:bg-[#265a42] disabled:opacity-50">
            <Upload size={16} /> {importing ? 'Importing...' : 'Import CSV'}
          </button>
          <input ref={fileInputRef} type="file" accept=".csv" onChange={handleImport} className="hidden" data-testid="subscribers-import-input" />
        </div>
      </div>

      {/* Search */}
      <div className="relative mb-4 max-w-sm">
        <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#b0ada6]" />
        <input
          data-testid="subscribers-search-input"
          type="text"
          placeholder="Search by email..."
          value={search}
          onChange={(e) => { setSearch(e.target.value); setPage(1); }}
          className="w-full pl-9 pr-8 py-2.5 border border-[#e7e3dd] rounded-lg text-sm focus:outline-none focus:border-[#2f6b4f] bg-white"
        />
        {search && (
          <button onClick={() => { setSearch(''); setPage(1); }} className="absolute right-3 top-1/2 -translate-y-1/2 text-[#b0ada6] hover:text-[#2d2d2d]">
            <X size={14} />
          </button>
        )}
      </div>

      {/* Table */}
      {loading ? (
        <div className="space-y-3">{[...Array(5)].map((_, i) => <div key={i} className="animate-pulse bg-[#fbfaf8] rounded-lg h-12" />)}</div>
      ) : subscribers.length === 0 ? (
        <div className="text-center py-12 bg-white border border-[#e7e3dd] rounded-xl">
          <Mail size={36} className="mx-auto text-[#c0c0c0] mb-3" />
          <p className="text-[#6f6a63] text-sm">{search ? 'No subscribers match your search.' : 'No subscribers yet.'}</p>
        </div>
      ) : (
        <>
          <div className="bg-white border border-[#e7e3dd] rounded-xl overflow-hidden">
            <div className="hidden md:grid md:grid-cols-[40px_1fr_200px_80px] items-center bg-[#fbfaf8] border-b border-[#e7e3dd] px-4 py-2.5">
              <span className="text-[10px] font-semibold tracking-wider uppercase text-[#6f6a63]">#</span>
              <span className="text-[10px] font-semibold tracking-wider uppercase text-[#6f6a63]">Email</span>
              <span className="text-[10px] font-semibold tracking-wider uppercase text-[#6f6a63]">Subscribed</span>
              <span className="text-[10px] font-semibold tracking-wider uppercase text-[#6f6a63] text-center">Actions</span>
            </div>
            {subscribers.map((s, idx) => (
              <div key={s._id} data-testid={`subscriber-row-${s._id}`} className={`grid grid-cols-1 md:grid-cols-[40px_1fr_200px_80px] items-center px-4 py-3 border-b border-[#f0efed] ${idx % 2 !== 0 ? 'bg-[#fafaf9]' : ''}`}>
                <span className="text-xs text-[#b0ada6] hidden md:block">{(page - 1) * 50 + idx + 1}</span>
                <span className="text-sm font-medium text-[#2d2d2d]" data-testid={`subscriber-email-${s._id}`}>{s.email}</span>
                <span className="text-xs text-[#6f6a63]">{formatDate(s.subscribed_at)}</span>
                <div className="flex justify-center">
                  <button data-testid={`subscriber-delete-${s._id}`} onClick={() => handleDelete(s._id)} className="p-1.5 hover:bg-red-50 rounded">
                    <Trash2 size={14} className="text-red-500" />
                  </button>
                </div>
              </div>
            ))}
          </div>
          {/* Pagination */}
          {totalPages > 1 && (
            <div className="flex justify-center gap-2 mt-4">
              {Array.from({ length: totalPages }, (_, i) => i + 1).map(p => (
                <button
                  key={p}
                  data-testid={`subscribers-page-${p}`}
                  onClick={() => setPage(p)}
                  className={`px-3 py-1.5 rounded-lg text-sm ${p === page ? 'bg-[#2d2d2d] text-white' : 'bg-white border border-[#e7e3dd] hover:bg-gray-50'}`}
                >
                  {p}
                </button>
              ))}
            </div>
          )}
        </>
      )}
    </div>
  );
}
